<template>
  <div class="container content">

    <div v-if="shared">
      <SharedHeader />
    </div>

    <div class="card">
      <section class="hero is-primary">
        <div class="hero-body">
          <div class="container">
            <h1 class="title">
              MFQA 2
            </h1>
          </div>
        </div>
      </section>
    </div>

    <div class="card">
      <div class=" card-header">
        <div class="card-header-title">
          Property Address
        </div>
        <div
          v-if="!shared"
          class="level-right is-small"
        >
          <a
            @click="goToProperty()"
            style="padding-left: 20px"
          >
            <i class="fas fa-arrow-left"></i>
            return to property</a>
          &nbsp;&nbsp;
          <button
            v-if="!shared"
            class="button is-small is-primary"
            @click="shareEvaluation($store.state.bpo2)"
          ><i class="fas fa-link"></i>&nbsp;&nbsp;Share MFQA 2</button>
          &nbsp;&nbsp;
        </div>
      </div>
      <div class="card-content">

        <div class="columns">

          <div class="column">

            <div v-if="$store.state.property.multifamilyAttributes.propertyName">
              <h2>{{ $store.state.property.multifamilyAttributes.propertyName }}</h2>
              <h4>{{ $store.state.property.address }}</h4>
              <h4>{{ $store.state.property.city }}, {{ $store.state.property.state }} {{ $store.state.property.zip }}</h4>
            </div>
            <div v-if="!$store.state.property.multifamilyAttributes.propertyName">
              <h3>{{ $store.state.property.address }}</h3>
              <h4>{{ $store.state.property.city }}, {{ $store.state.property.state }} {{ $store.state.property.zip }}</h4>
            </div>
          </div>

          <div class="column">
            <div class="content"></div>
            <div class="level">
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Units</p>
                  <p class="subtitle">&nbsp;{{ $store.state.property.multifamilyAttributes.units }}&nbsp;</p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Year Built</p>
                  <p class="subtitle">&nbsp;{{ $store.state.property.multifamilyAttributes.yearBuilt }}&nbsp;</p>
                </div>
              </div>
              <div class="level-item has-text-centered">
                <div>
                  <p class="heading">Class</p>
                  <p class="subtitle">&nbsp;{{ $store.state.property.multifamilyAttributes.propertyClass }}&nbsp;</p>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>

    <div class="card">
      <section class="hero is-info">
        <div class="hero-body">
          <div class="container">
            <h1 class="title">
              Worksheet
            </h1>
          </div>
        </div>
      </section>
    </div>

    <div class="card">
      <div class="card-content">

        <div v-if="shared && readonly">
          <b-message
            type="is-info"
            has-icon
          >
            <div class="subtitle">View Only - this shared evaluation is in view-only mode, changes are not allowed.</div>
          </b-message>
          <p></p>
        </div>

        <div v-if="shared && !readonly">
          <b-message
            type="is-info"
            has-icon
          >
            <div class="subtitle">Edit - this shared evaluation is in edit mode, any changes you make will update the evaluation for everyone.</div>
          </b-message>
          <p></p>
        </div>

        <div class="tile is-ancestor is-size-7">
          <div class="tile is-6 is-vertical is-parent">
            <div class="tile is-child box">
              <!-- TOP LEFT -->
              <p class="title">Deal Terms</p>

              <table class="table is-fullwidth">

                <tr>
                  <td
                    colspan="3"
                    style="border: none !important;"
                  >
                  </td>
                </tr>

                <tr>
                  <td>Asking Price
                    <b-tooltip
                      type="is-info"
                      label="The price the seller is asking for. This isn't used in any calculations so you can leave it blank, but it's nice to have for informational purposes."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.askingPrice_J18"
                        @input="calculate('inputs.askingPrice_J18')"
                        :options="currency"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.askingPricePerUnit_K18 | currency(0) }} per unit</td>

                </tr>

                <tr>

                  <td>Offer Price
                    <b-tooltip
                      type="is-info"
                      label="The price you will offer to purchase the property. The Cap Rate % option will calculate an offer price based on a Cap Rate. The Cap Rate is asset class specific -- a Mentor, Broker, or Seller may provide this to you. The proforma year 1 Net ROI is the percentage of ROI based on your cash invested. The proforma year 1 DSCR (Debt Service Coverage Ratio) is the ratio of cash available to debt servicing for interest, principal and lease payments."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="align-right">{{ $store.state.bpo2.outputs.offerPrice_J19 | currency(0) }}</div>

                    <div style="padding-top: 1em">

                      <span>
                        <b-radio
                          v-model="$store.state.bpo2.inputs.offerPriceCalcType"
                          native-value="Raw"
                          @input="calculate('inputs.offerPriceCalcType')"
                          :disabled="readonly"
                        >
                          Actual offer price
                        </b-radio>

                        <div class="level-right">
                          <vue-autonumeric
                            :disabled="$store.state.bpo2.inputs.offerPriceCalcType != 'Raw'"
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="[ ($store.state.bpo2.inputs.offerPriceCalcType != 'Raw') ? {} : { backgroundColor: inputBackgroundColor }]"
                            v-model="$store.state.bpo2.inputs.offerPrice_J19"
                            @input="calculate('inputs.offerPrice_J19')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </div>
                      </span>

                      <span>
                        <b-radio
                          v-model="$store.state.bpo2.inputs.offerPriceCalcType"
                          native-value="CapRate"
                          @input="calculate('inputs.offerPriceCalcType')"
                          :disabled="readonly"
                        >
                          Calculate based on actual Cap Rate
                        </b-radio>
                        <div class="level-right">
                          <vue-autonumeric
                            :disabled="$store.state.bpo2.inputs.offerPriceCalcType != 'CapRate'"
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="[ ($store.state.bpo2.inputs.offerPriceCalcType != 'CapRate') ? {} : { backgroundColor: inputBackgroundColor }]"
                            v-model="$store.state.bpo2.inputs.offerPriceCapRate"
                            @input="calculate('inputs.offerPriceCapRate')"
                            :options="percentage"
                            :readonly="readonly"
                          />
                        </div>
                      </span>

                      <span>
                        <b-radio
                          v-model="$store.state.bpo2.inputs.offerPriceCalcType"
                          native-value="Dscr"
                          @input="calculate('inputs.offerPriceCalcType')"
                          :disabled="readonly"
                        >
                          Calculate based on proforma Debt Service Coverage Ratio (DSCR)
                        </b-radio>
                        <div class="level-right">
                          <vue-autonumeric
                            :disabled="$store.state.bpo2.inputs.offerPriceCalcType != 'Dscr'"
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="[ ($store.state.bpo2.inputs.offerPriceCalcType != 'Dscr') ? {} : { backgroundColor: inputBackgroundColor }]"
                            v-model="$store.state.bpo2.inputs.offerPriceTargetDebtServiceCoverageRatio"
                            @input="calculate('inputs.offerPriceTargetDebtServiceCoverageRatio')"
                            :options="decimal"
                            :readonly="readonly"
                          />
                        </div>
                      </span>

                      <span>
                        <b-radio
                          v-model="$store.state.bpo2.inputs.offerPriceCalcType"
                          native-value="NetRoi"
                          @input="calculate('inputs.offerPriceCalcType')"
                          :disabled="readonly"
                        >
                          Calculate based on proforma Net ROI
                        </b-radio>
                        <div class="level-right">
                          <vue-autonumeric
                            :disabled="$store.state.bpo2.inputs.offerPriceCalcType != 'NetRoi'"
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="[ ($store.state.bpo2.inputs.offerPriceCalcType != 'NetRoi') ? {} : { backgroundColor: inputBackgroundColor }]"
                            v-model="$store.state.bpo2.inputs.offerPriceTargetNetReturnOnInvestment"
                            @input="calculate('inputs.offerPriceTargetNetReturnOnInvestment')"
                            :options="percentage"
                            :readonly="readonly"
                          />
                        </div>
                      </span>

                    </div>

                  </td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.offerPricePerUnit_K19 | currency(0) }} per unit</td>

                </tr>

                <tr>

                  <td>Rehab - In Loan</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.rehabInLoan_J20 | currency(0) }}</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.rehabInLoanPerUnit_K20 | currency(0) }} per unit</td>

                </tr>

                <tr>

                  <td>Total "In"</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.totalIn_J21 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Closing & Start-up Costs
                    <b-tooltip
                      type="is-info"
                      label="The percentage of the Total In amount that will be used for the costs of closing the deal. A conservative place to start is 6%."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.closingAndStartupCosts_J22 | currency(0) }}</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.closingAndStartupCostsPercent_I22 | percent(1) }}</td>

                </tr>

                <tr>

                  <td>Operating Capital
                    <b-tooltip
                      type="is-info"
                      label="Amount set aside for initial Operating Capital expenses. Guideline is MINIMUM 1 month expense and debt service. Recommended amount is 2 months expense and debt service. If you turn on Calculate then 2 months will be calculated automatically."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>

                  </td>

                  <td>
                    <div class="align-right">{{ $store.state.bpo2.outputs.operatingCapital_J23 | currency(0) }}</div>

                    <div style="padding-top: 1em">

                      <span>

                        <b-radio
                          v-model="$store.state.bpo2.inputs.operatingCapitalCalcType"
                          native-value="Raw"
                          @input="calculate('inputs.operatingCapitalCalcType')"
                          :disabled="readonly"
                        >
                          Actual operating capital
                        </b-radio>

                        <div class="level-right">
                          <vue-autonumeric
                            :disabled="$store.state.bpo2.inputs.operatingCapitalCalcType != 'Raw'"
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="[ ($store.state.bpo2.inputs.operatingCapitalCalcType != 'Raw') ? {} : { backgroundColor: inputBackgroundColor }]"
                            v-model="$store.state.bpo2.inputs.operatingCapital_J23"
                            @input="calculate('inputs.operatingCapital_J23')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </div>

                      </span>

                      <span>
                        <b-radio
                          v-model="$store.state.bpo2.inputs.operatingCapitalCalcType"
                          native-value="Calculated"
                          @input="calculate('inputs.operatingCapitalCalcType')"
                          :disabled="readonly"
                        >
                          Calculate based on 2 months expenses
                        </b-radio>

                      </span>

                    </div>

                  </td>

                  <td></td>

                </tr>

                <tr>

                  <td>Rehab - Not In Loan</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.rehabNotInLoan_J24 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Prepaid Expenses and Deposits</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.prepaidExpensesAndDeposits_J25 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Total Out Of Pocket</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.totalOutOfPocket_J26 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>
                  <td>&nbsp;</td>
                  <td></td>
                  <td></td>
                </tr>

                <tr>

                  <td>#/Units</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.numberOfUnits_J28 }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Total "In"$/Unit</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.totalInPricePerUnit_J29 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Down Payment
                    <b-tooltip
                      type="is-info"
                      label="Percentage of the Total In amount that estimates the down payment. Usually around 20% to 40%."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.downPaymentPercent_J30"
                        @input="calculate('inputs.downPaymentPercent_J30')"
                        :options="percentage"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.downPayment_K30 | currency(0) }}</td>

                </tr>

                <tr>

                  <td>Loan Amount</td>

                  <td class="align-right">{{ $store.state.bpo2.outputs.loanAmount_J31 | currency(0) }}</td>

                  <td></td>

                </tr>

                <tr>

                  <td>Interest Rate
                    <b-tooltip
                      type="is-info"
                      label="The interest rate of the loan."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.interestRatePercent_J32"
                        @input="calculate('inputs.interestRatePercent_J32')"
                        :options="percentage"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td></td>

                </tr>

                <tr>

                  <td>Years Amortization
                    <b-tooltip
                      type="is-info"
                      label="How many years the loan is for."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.termInYears_J33"
                        @input="calculate('inputs.termInYears_J33')"
                        :options="years"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td></td>

                </tr>

                <tr>

                  <td>Interest Only Period in Years
                    <b-tooltip
                      type="is-info"
                      label="How many years the loan payments are interest only. This can be partial years, for example 1.5 is valid."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.interestOnlyPeriodInYears_J34"
                        @input="calculate('inputs.interestOnlyPeriodInYears_J34')"
                        :options="years"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td></td>

                </tr>

                <tr>

                  <td>% of Rehab to be Financed
                    <b-tooltip
                      type="is-info"
                      label="The percentage of the rehab that will be financed with borrowed money."
                      multilined
                    >
                      <i class="far fa-question-circle fa-lg"></i>
                    </b-tooltip>
                  </td>

                  <td>
                    <div class="level-right">
                      <vue-autonumeric
                        class="input is-small"
                        style="text-align: right"
                        v-bind:style="{ backgroundColor: inputBackgroundColor }"
                        v-model="$store.state.bpo2.inputs.percentOfRehabToBeFinanced_J35"
                        @input="calculate('inputs.percentOfRehabToBeFinanced_J35')"
                        :options="percentage"
                        :readonly="readonly"
                      />
                    </div>
                  </td>

                  <td></td>

                </tr>

              </table>

            </div>

            <div class="tile is-child box">
              <!-- MIDDLE LEFT -->
              <p class="title">Rents</p>

              <div class="level">

                <button
                  class="button is-primary is-small"
                  @click="addRentLine()"
                  :disabled="readonly"
                >Add Rent</button>

                <div>
                  <b-field>
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.rentIncreasePercent_W26"
                      @input="calculate('inputs.proformasYear1.rentIncreasePercent_W26')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                    <p class="control">
                      <span
                        class="button is-primary is-small"
                        @click="updateRentIncreasePercent()"
                        :disabled="readonly"
                      >Increase Rent</span>
                    </p>
                  </b-field>
                </div>

                <div class="level">
                  <button
                    class="button is-info is-small"
                    @click="showRentRangeComps()"
                    :disabled="readonly"
                  >Get Scheduled Rents from RentRange</button>
                  <b-tooltip
                    style="margin-left: 0.4em"
                    type="is-info"
                    label="Quickly set the Scheduled Rent $ column by looking up market rents on RentRange. This is a valid option when you are doing a 'back of the napkin' calculation. You can replace these values later after you do a deeper market survey."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </div>

              </div>

              <section
                class="section"
                v-if="$store.state.bpo2.inputs.rents.length == 0"
              >
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon
                      icon="emoticon-happy"
                      size="is-large"
                    >
                    </b-icon>
                  </p>
                  <p>No rents yet, please add them</p>
                </div>
              </section>

              <table class="table is-fullwidth is-expanded">

                <tr>
                  <th style="vertical-align: bottom; text-align: left">Type</th>
                  <th style="vertical-align: bottom; text-align: right">#</th>
                  <th style="vertical-align: bottom; text-align: right">Sqft</th>
                  <th style="vertical-align: bottom; text-align: right">Scheduled Rent</th>
                  <th style="vertical-align: bottom; text-align: right">Proforma Rent</th>
                  <th></th>
                  <th></th>
                </tr>

                <tr
                  v-for="(line, index) in $store.state.bpo2.inputs.rents"
                  v-bind:key="index"
                  class="row"
                >

                  <td>
                    <select
                      class="select is-small"
                      placeholder=""
                      v-model="line.type"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      @input="calculate('inputs.rents')"
                      :disabled="readonly"
                    >
                      <option
                        v-for="(unitType, index) in $store.state.bpoUnitTypes"
                        :value="index"
                        :key="unitType"
                      >
                        {{ unitType }}
                      </option>
                    </select>
                  </td>

                  <td>
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="line.unitCount"
                      @input="calculate('inputs.rents')"
                      :options="number"
                      :readonly="readonly"
                    />
                  </td>

                  <td>
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="line.sqft"
                      @input="calculate('inputs.rents')"
                      :options="number"
                      :readonly="readonly"
                    />
                  </td>

                  <td>
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="line.currentRent"
                      @input="calculate('inputs.rents')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>

                  <td>
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="line.proformaRent"
                      @blur="updateProformaGrossRentalIncome()"
                      @input="calculate('inputs.rents')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>

                  <td style="padding-left: 0px;padding-right: 0px">
                    <button
                      class="button is-small"
                      @click="addRentLine(index)"
                      round
                      :disabled="readonly"
                    ><i class="fas fa-plus"></i></button>
                  </td>

                  <td style="padding-left: 0px;padding-right: 0px">
                    <button
                      class="button is-small"
                      @click="removeRentLine(index)"
                      round
                      :disabled="readonly"
                    ><i class="fas fa-trash"></i></button>
                  </td>

                </tr>

              </table>

              <p class="subtitle">Scheduled Rent Grid</p>

              <table class="table is-fullwidth">
                <tr>
                  <td class="header">Type</td>
                  <td class="header align-center">#</td>
                  <td class="header align-center">Rent</td>
                  <td class="header align-center">Sqft</td>
                  <td class="header align-center">$/Sqft</td>
                  <td class="header align-center">Mthly</td>
                  <td class="header align-center">Annual</td>
                </tr>

                <tr
                  v-for="(line, index) in $store.state.bpo2.outputs.rents"
                  v-bind:key="index"
                  class="row"
                >
                  <td>{{ line.type }}</td>
                  <td class="align-center">{{ line.unitCount }}</td>
                  <td class="align-center">{{ line.currentRent | currency(0) }}</td>
                  <td class="align-center">{{ line.sqft | number(0) }}</td>
                  <td class="align-center">{{ line.currentPricePerSqft | currency(2) }}</td>
                  <td class="align-center">{{ line.currentMonthly | currency(0) }}</td>
                  <td class="align-center">{{ line.currentAnnual | currency(0) }}</td>
                </tr>

                <tr class="subtotal1">
                  <td class="footer align-center"></td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalUnitCount_R23 }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalCurrentRent_S23 | currency(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalSqft_T23 | number(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalCurrentPricePerSqft_U23 | currency(2) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalCurrentMonthly_V23 | currency(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalCurrentAnnual_W23 | currency(0) }}</td>
                </tr>

              </table>

              <p class="subtitle">Proforma Rent Grid</p>

              <table class="table is-fullwidth">
                <tr>
                  <td class="header">Type</td>
                  <td class="header align-center">#</td>
                  <td class="header align-center">Rent</td>
                  <td class="header align-center">Sqft</td>
                  <td class="header align-center">$/Sqft</td>
                  <td class="header align-center">Mthly</td>
                  <td class="header align-center">Annual</td>
                </tr>

                <tr
                  v-for="(line, index) in $store.state.bpo2.outputs.rents"
                  v-bind:key="index"
                  class="row"
                >
                  <td>{{ line.type }}</td>
                  <td class="align-center">{{ line.unitCount }}</td>
                  <td class="align-center">{{ line.proformaRent | currency(0) }}</td>
                  <td class="align-center">{{ line.sqft }}</td>
                  <td class="align-center">{{ line.proformaPricePerSqft | currency(2) }}</td>
                  <td class="align-center">{{ line.proformaMonthly | currency(0) }}</td>
                  <td class="align-center">{{ line.proformaAnnual | currency(0) }}</td>
                </tr>

                <tr class="subtotal1">
                  <td class="footer align-center"></td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalUnitCount_R23 }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalProformaRent_S37 | currency(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalSqft_T23 | number(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalProformaPricePerSqft_U37 | currency(2) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalProformaMonthly_V37 | currency(0) }}</td>
                  <td class="footer align-center">{{ this.$store.state.bpo2.outputs.totalProformaAnnual_W37 | currency(0) }}</td>
                </tr>

              </table>
            </div>

            <div class="tile is-child box">

              <p class="title">Notes</p>

              <div>
                <vue-editor
                  :editorToolbar="customToolbar"
                  v-model="$store.state.bpo2.inputs.notes_H80"
                  @input="calculate('inputs.notes_H80')"
                  :disabled="readonly"
                ></vue-editor>
              </div>

            </div>

          </div>

          <div class="tile is-parent">

            <div class="tile is-child box">
              <!-- BOTTOM LEFT -->
              <p class="title">Expenses</p>

              <p class="subtitle">Operating Expenses</p>

              <b-field label="Market">
                <select
                  class="select is-small"
                  placeholder="Select a market for NAA values"
                  v-model="$store.state.bpo2.inputs.naaSurveyMarketId"
                  v-bind:style="{ backgroundColor: inputBackgroundColor }"
                  @change="updateMarket"
                  :disabled="readonly"
                >
                  <option
                    v-for="(line, index) of $store.state.naaSurveys"
                    :key="index"
                    :value="line.marketId"
                  >
                    {{ line.marketDescription }}
                  </option>
                </select>
              </b-field>

              <table class="table is-fullwidth">

                <tr>
                  <td class="operating-expenses-columns-header align-left">Category</td>
                  <td class="operating-expenses-columns-header align-right">{{ $store.state.bpo2.outputs.actuals.label_J38 }} $</td>
                  <td class="operating-expenses-columns-header align-right">$/Unit</td>
                  <td class="operating-expenses-columns-header align-right">Proforma $</td>
                  <td class="operating-expenses-columns-header align-right">$/Unit</td>
                  <td class="operating-expenses-columns-header align-right">NAA $/Unit</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Taxes</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.taxesActual"
                      @input="calculate('inputs.operatingExpenses.taxesActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.taxesActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.taxesProforma"
                      @input="calculate('inputs.operatingExpenses.taxesProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.taxesProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.taxesPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Salaries and Personnel</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.salariesAndPersonnelActual"
                      @input="calculate('inputs.operatingExpenses.salariesAndPersonnelActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.salariesAndPersonnelActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.salariesAndPersonnelProforma"
                      @input="calculate('inputs.operatingExpenses.salariesAndPersonnelProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.salariesAndPersonnelProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.salariesAndPersonnelPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Repair and Maintenance</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.repairAndMaintenanceActual"
                      @input="calculate('inputs.operatingExpenses.repairAndMaintenanceActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.repairAndMaintenanceActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.repairAndMaintenanceProforma"
                      @input="calculate('inputs.operatingExpenses.repairAndMaintenanceProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.repairAndMaintenanceProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.repairAndMaintenancePricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Management Fees</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.managementFeesActual"
                      @input="calculate('inputs.operatingExpenses.managementFeesActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.managementFeesActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.managementFeesProforma"
                      @input="calculate('inputs.operatingExpenses.managementFeesProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.managementFeesProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.managementFeesPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Contract Services</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.contractServicesActual"
                      @input="calculate('inputs.operatingExpenses.contractServicesActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.contractServicesActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.contractServicesProforma"
                      @input="calculate('inputs.operatingExpenses.contractServicesProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.contractServicesProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.contractServicesPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Utilities</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.utilitiesActual"
                      @input="calculate('inputs.operatingExpenses.utilitiesActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.utilitiesActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.utilitiesProforma"
                      @input="calculate('inputs.operatingExpenses.utilitiesProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.utilitiesProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.utilitiesPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Administrative</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.administrativeActual"
                      @input="calculate('inputs.operatingExpenses.administrativeActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.administrativeActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.administrativeProforma"
                      @input="calculate('inputs.operatingExpenses.administrativeProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.administrativeProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.adminitrativePricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Insurance</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.insuranceActual"
                      @input="calculate('inputs.operatingExpenses.insuranceActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.insuranceActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.insuranceProforma"
                      @input="calculate('inputs.operatingExpenses.insuranceProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.insuranceProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.insurancePricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Marketing</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.marketingActual"
                      @input="calculate('inputs.operatingExpenses.marketingActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.marketingActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.marketingProforma"
                      @input="calculate('inputs.operatingExpenses.marketingProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.marketingProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">{{ $store.state.bpo2.outputs.naaSurvey.marketingPricePerUnit | currency(0) }}</td>
                  <td></td>
                </tr>

                <tr>
                  <td>Delete - Carried Over</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.carriedOverActual"
                      @input="calculate('inputs.operatingExpenses.carriedOverActual')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.carriedOverActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.operatingExpenses.carriedOverProforma"
                      @input="calculate('inputs.operatingExpenses.carriedOverProforma')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ $store.state.bpo2.inputs.operatingExpenses.carriedOverProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">$0</td>
                  <td>
                    <button
                      class="button is-small"
                      @click="addOperatingExpensesLine($store.state.bpo2.inputs.operatingExpenses.customList)"
                      round
                      :disabled="readonly"
                    ><i class="fas fa-plus"></i></button>
                  </td>
                </tr>

                <tr
                  v-for="(expense, index) in $store.state.bpo2.inputs.operatingExpenses.customList"
                  :key="index"
                  :value="expense"
                >
                  <td>
                    <input
                      v-model="expense.name"
                      class="input is-small"
                      style="background-color: #ffdd57 !important;"
                      @input="calculate('inputs.operatingExpenses.customList')"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="expense.amountActual"
                      @input="calculate('inputs.operatingExpenses.customList')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ expense.amountActual / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="expense.amountProforma"
                      @input="calculate('inputs.operatingExpenses.customList')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </td>
                  <td class="align-right">{{ expense.amountProforma / $store.state.bpo2.outputs.numberOfUnits_J28 | currency(0) }}</td>
                  <td class="align-right">$0</td>
                  <td>
                    <div class="level level-left">
                      <button
                        class="button is-small"
                        @click="addOperatingExpensesLine($store.state.bpo2.inputs.operatingExpenses.customList)"
                        round
                        :disabled="readonly"
                      ><i class="fas fa-plus"></i></button>
                      <button
                        class="button is-small"
                        @click="removeLine($store.state.bpo2.inputs.operatingExpenses.customList, index, 'inputs.operatingExpenses.customList')"
                        round
                        :disabled="readonly"
                      ><i class="fas fa-trash"></i></button>
                    </div>
                  </td>
                </tr>

                <tr class="subtotal1">
                  <td class="footer align-left">TOTAL</td>
                  <td class="footer align-right">{{ this.$store.state.bpo2.outputs.operatingExpenses.totalActual_S57 | currency(0) }}</td>
                  <td class="footer align-right">{{ this.$store.state.bpo2.outputs.operatingExpenses.totalActualPerUnit_T57 | currency(0) }}</td>
                  <td class="footer align-right">{{ this.$store.state.bpo2.outputs.operatingExpenses.totalProforma_U57 | currency(0) }}</td>
                  <td class="footer align-right">{{ this.$store.state.bpo2.outputs.operatingExpenses.totalProformaPerUnit_V57 | currency(0) }}</td>
                  <td class="footer align-right">{{ this.$store.state.bpo2.outputs.operatingExpenses.totalNaaSurveyPerUnit_W57 | currency(0) }}</td>
                  <td></td>
                  <td></td>
                </tr>

              </table>

              <div class="tile is-ancestor">
                <div class="tile is-parent">
                  <div class="tile is-child">
                    <p class="subtitle">Capital Improvements</p>

                    <table class="table is-fullwidth">

                      <tr>
                        <td>Siding</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.siding"
                            @input="calculate('inputs.capitalImprovements.siding')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Ext Paint</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.exteriorPaint"
                            @input="calculate('inputs.capitalImprovements.exteriorPaint')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>HVAC Replacement</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.hvacReplacement"
                            @input="calculate('inputs.capitalImprovements.hvacReplacement')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Remodel Office</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.remodelOffice"
                            @input="calculate('inputs.capitalImprovements.remodelOffice')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Office Equipment</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.officeEquipment"
                            @input="calculate('inputs.capitalImprovements.officeEquipment')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Jet Sewers</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.jetSewers"
                            @input="calculate('inputs.capitalImprovements.jetSewers')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Parking Lot</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.parkingLot"
                            @input="calculate('inputs.capitalImprovements.parkingLot')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Upgrade Make-Readies</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.upgradeMakeReadies"
                            @input="calculate('inputs.capitalImprovements.upgradeMakeReadies')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Signage</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.signage"
                            @input="calculate('inputs.capitalImprovements.signage')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Trees</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.trees"
                            @input="calculate('inputs.capitalImprovements.trees')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Landscaping</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.landscaping"
                            @input="calculate('inputs.capitalImprovements.landscaping')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Boilers</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.boilers"
                            @input="calculate('inputs.capitalImprovements.boilers')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Pools</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.pools"
                            @input="calculate('inputs.capitalImprovements.pools')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Roof</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.roof"
                            @input="calculate('inputs.capitalImprovements.roof')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Maintenance Equipment</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.maintenanceEquipment"
                            @input="calculate('inputs.capitalImprovements.maintenanceEquipment')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Foundation</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.foundation"
                            @input="calculate('inputs.capitalImprovements.foundation')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Delete - Carried Over</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.capitalImprovements.carriedOver"
                            @input="calculate('inputs.capitalImprovements.carriedOver')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <button
                            class="button is-small"
                            @click="addLine($store.state.bpo2.inputs.capitalImprovements.customList, 'inputs.capitalImprovements.customList')"
                            round
                            :disabled="readonly"
                          ><i class="fas fa-plus"></i></button>
                        </td>
                      </tr>

                      <tr
                        v-for="(expense, index) in $store.state.bpo2.inputs.capitalImprovements.customList"
                        :key="index"
                        :value="expense"
                      >
                        <td>
                          <input
                            v-model="expense.name"
                            class="input is-small"
                            style="background-color: #ffdd57 !important;"
                            @input="calculate('inputs.capitalImprovements.customList')"
                            :readonly="readonly"
                          />
                        </td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="expense.amount"
                            @input="calculate('inputs.capitalImprovements.customList')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <div class="level level-left">
                            <button
                              class="button is-small"
                              @click="addLine($store.state.bpo2.inputs.capitalImprovements.customList, 'inputs.capitalImprovements.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-plus"></i></button>
                            <button
                              class="button is-small"
                              @click="removeLine($store.state.bpo2.inputs.capitalImprovements.customList, index, 'inputs.capitalImprovements.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-trash"></i></button>
                          </div>
                        </td>
                      </tr>

                      <tr class="subtotal1">
                        <td class="footer align-left">TOTAL</td>
                        <td class="footer align-right">{{ $store.state.bpo2.outputs.capitalImprovements.total_S89 | currency(0) }}</td>
                        <td></td>
                        <td></td>
                      </tr>

                    </table>
                  </div>
                </div>

                <div class="tile is-6 is-vertical is-parent">
                  <div class="tile is-child">
                    <p class="subtitle">Closing Costs</p>

                    <table class="table is-fullwidth">

                      <tr>
                        <td>Lender Fees</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.closingCosts.lenderFees"
                            @input="calculate('inputs.closingCosts.lenderFees')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Appraisal</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.closingCosts.appraisal"
                            @input="calculate('inputs.closingCosts.appraisal')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Legal</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.closingCosts.legal"
                            @input="calculate('inputs.closingCosts.legal')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Brokerage Fees</td>

                        <td>
                          <div class="align-right">{{ getBrokerageFees() | currency(0) }}</div>
                          <div class="align-right">&nbsp;</div>

                          <span>
                            <b-radio
                              v-model="$store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType"
                              native-value="Raw"
                              @input="calculate('inputs.offerPriceCalcType')"
                              :disabled="readonly"
                            >
                              Actual brokerage fee
                            </b-radio>

                            <div class="level-right">
                              <vue-autonumeric
                                :disabled="$store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType != 'Raw'"
                                class="input is-small"
                                style="text-align: right"
                                v-bind:style="[ ($store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType != 'Raw') ? {} : { backgroundColor: inputBackgroundColor }]"
                                v-model="$store.state.bpo2.inputs.closingCosts.brokerageFees"
                                @input="calculate('inputs.closingCosts.brokerageFees')"
                                :options="currency"
                                :readonly="readonly"
                              />
                            </div>

                          </span>

                          <span>
                            <b-radio
                              v-model="$store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType"
                              native-value="Calculated"
                              @input="calculate('inputs.closingCosts.brokerageFeesCalcType')"
                              :disabled="readonly"
                            >
                              Percent of offer price
                            </b-radio>

                            <div class="level-right">

                              <div class="level">
                                <vue-autonumeric
                                  :disabled="$store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType != 'Calculated'"
                                  class="input is-small"
                                  style="text-align: right"
                                  v-bind:style="[ ($store.state.bpo2.inputs.closingCosts.brokerageFeesCalcType != 'Calculated') ? {} : { backgroundColor: inputBackgroundColor }]"
                                  v-model="$store.state.bpo2.inputs.closingCosts.brokerageFeesPercentOfferPrice"
                                  @input="calculate('inputs.closingCosts.brokerageFeesPercentOfferPrice')"
                                  :options="percentage"
                                  :readonly="readonly"
                                />
                              </div>

                            </div>

                          </span>

                        </td>

                        <td></td>

                        <!-- <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-model="$store.state.bpo2.inputs.closingCosts.brokerageFees"
                            @input="calculate('inputs.closingCosts.brokerageFees')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td> -->
                      </tr>

                      <tr>
                        <td>Title Fees</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.closingCosts.titleFees"
                            @input="calculate('inputs.closingCosts.titleFees')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Delete - Carried Over</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.closingCosts.carriedOver"
                            @input="calculate('inputs.closingCosts.carriedOver')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <button
                            class="button is-small"
                            @click="addLine($store.state.bpo2.inputs.closingCosts.customList, 'inputs.closingCosts.customList')"
                            round
                            :disabled="readonly"
                          ><i class="fas fa-plus"></i></button>
                        </td>
                      </tr>

                      <tr
                        v-for="(expense, index) in $store.state.bpo2.inputs.closingCosts.customList"
                        :key="index"
                        :value="expense"
                      >
                        <td>
                          <input
                            class="input is-small"
                            style="background-color: #ffdd57 !important;"
                            v-model="expense.name"
                            :readonly="readonly"
                          />
                        </td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="expense.amount"
                            @input="calculate('inputs.closingCosts.customList')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <div class="level level-left">
                            <button
                              class="button is-small"
                              @click="addLine($store.state.bpo2.inputs.closingCosts.customList, 'inputs.closingCosts.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-plus"></i></button>
                            <button
                              class="button is-small"
                              @click="removeLine($store.state.bpo2.inputs.closingCosts.customList, index, 'inputs.closingCosts.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-trash"></i></button>
                          </div>
                        </td>
                      </tr>

                      <tr class="subtotal1">
                        <td class="footer align-left">TOTAL</td>
                        <td class="footer align-right">{{ $store.state.bpo2.outputs.closingCosts.total_W68 | currency(0) }}</td>
                        <td></td>
                        <td></td>
                      </tr>

                    </table>

                  </div>

                  <div class="tile is-child">
                    <p class="subtitle">Start-up Costs</p>

                    <table class="table is-fullwidth">

                      <tr>
                        <td>Sewer Camera</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.startupCosts.sewerCamera"
                            @input="calculate('inputs.startupCosts.sewerCamera')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Permits/Permit Checks</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.startupCosts.permits"
                            @input="calculate('inputs.startupCosts.permits')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>WDI Inspection</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.startupCosts.wdiInspection"
                            @input="calculate('inputs.startupCosts.wdiInspection')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Survey</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.startupCosts.survey"
                            @input="calculate('inputs.startupCosts.survey')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Environmental</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.startupCosts.environmental"
                            @input="calculate('inputs.startupCosts.environmental')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <button
                            class="button is-small"
                            @click="addLine($store.state.bpo2.inputs.startupCosts.customList, 'inputs.startupCosts.customList')"
                            round
                            :disabled="readonly"
                          ><i class="fas fa-plus"></i></button>
                        </td>
                      </tr>

                      <tr
                        v-for="(expense, index) in $store.state.bpo2.inputs.startupCosts.customList"
                        :key="index"
                        :value="expense"
                      >
                        <td>
                          <input
                            v-model="expense.name"
                            class="input is-small"
                            style="background-color: #ffdd57 !important;"
                            :readonly="readonly"
                          />
                        </td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="expense.amount"
                            @input="calculate('inputs.startupCosts.customList')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <div class="level level-left">
                            <button
                              class="button is-small"
                              @click="addLine($store.state.bpo2.inputs.startupCosts.customList, 'inputs.startupCosts.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-plus"></i></button>
                            <button
                              class="button is-small"
                              @click="removeLine($store.state.bpo2.inputs.startupCosts.customList, index, 'inputs.startupCosts.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-trash"></i></button>
                          </div>
                        </td>
                      </tr>

                      <tr class="subtotal1">
                        <td class="footer align-left">TOTAL</td>
                        <td class="footer align-right">{{ $store.state.bpo2.outputs.startupCosts.total_W77 | currency(0) }}</td>
                        <td></td>
                        <td></td>
                      </tr>

                    </table>

                  </div>

                  <div class="tile is-child">
                    <p class="subtitle">Prepaids & Deposits</p>
                    <table class="table is-fullwidth">

                      <tr>
                        <td>Insurance</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.insurance"
                            @input="calculate('inputs.prepaidsAndDeposits.insurance')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Property Taxes</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.propertyTaxes"
                            @input="calculate('inputs.prepaidsAndDeposits.propertyTaxes')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Water/Sewer Deposit</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.waterSewerDeposit"
                            @input="calculate('inputs.prepaidsAndDeposits.waterSewerDeposit')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Electric Deposit</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.electricDeposit"
                            @input="calculate('inputs.prepaidsAndDeposits.electricDeposit')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Gas Deposit</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.gasDeposit"
                            @input="calculate('inputs.prepaidsAndDeposits.gasDeposit')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td></td>
                      </tr>

                      <tr>
                        <td>Replacement Reserve</td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="$store.state.bpo2.inputs.prepaidsAndDeposits.replacementReserve"
                            @input="calculate('inputs.prepaidsAndDeposits.replacementReserve')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <button
                            class="button is-small"
                            @click="addLine($store.state.bpo2.inputs.prepaidsAndDeposits.customList, 'inputs.prepaidsAndDeposits.customList')"
                            round
                            :disabled="readonly"
                          ><i class="fas fa-plus"></i></button>
                        </td>
                      </tr>

                      <tr
                        v-for="(expense, index) in $store.state.bpo2.inputs.prepaidsAndDeposits.customList"
                        :key="index"
                        :value="expense"
                      >
                        <td>
                          <input
                            v-model="expense.name"
                            class="input is-small"
                            style="background-color: #ffdd57 !important;"
                            :readonly="readonly"
                          />
                        </td>
                        <td class="align-right">
                          <vue-autonumeric
                            class="input is-small"
                            style="text-align: right"
                            v-bind:style="{ backgroundColor: inputBackgroundColor }"
                            v-model="expense.amount"
                            @input="calculate('inputs.prepaidsAndDeposits.customList')"
                            :options="currency"
                            :readonly="readonly"
                          />
                        </td>
                        <td>
                          <div class="level level-left">
                            <button
                              class="button is-small"
                              @click="addLine($store.state.bpo2.inputs.prepaidsAndDeposits.customList, 'inputs.prepaidsAndDeposits.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-plus"></i></button>
                            <button
                              class="button is-small"
                              @click="removeLine($store.state.bpo2.inputs.prepaidsAndDeposits.customList, index, 'inputs.prepaidsAndDeposits.customList')"
                              round
                              :disabled="readonly"
                            ><i class="fas fa-trash"></i></button>
                          </div>
                        </td>
                      </tr>

                      <tr class="subtotal1">
                        <td class="footer align-left">TOTAL</td>
                        <td class="footer align-right">{{ $store.state.bpo2.outputs.prepaidsAndDeposits.total_W89 | currency(0) }}</td>
                        <td></td>
                        <td></td>
                      </tr>

                    </table>

                  </div>
                </div>

              </div>

            </div>

          </div>

        </div>

        <div class="tile is-size-7">
          <div class="tile is-child box">
            <!-- RIGHT-->
            <div class="level">
              <div class="level-left">
                <p class="title">Summary Projection</p>
              </div>
              <div class="level-right">
                <span>Show columns for Proforma Year 4 and 5?&nbsp;</span>
                <b-switch
                  v-model="$store.state.bpo2.inputs.showProformasYear4And5"
                  @input="calculate('inputs.showProformasYear4And5')"
                  :disabled="readonly"
                >
                </b-switch>
              </div>
            </div>

            <table class="table is-fullwidth">

              <tr>
                <td class="col1">Income Change
                  <b-tooltip
                    type="is-info"
                    label="The percentage increase of income over the prior year."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear2IncomeChange_N32"
                      @input="calculate('inputs.proformaYear2IncomeChange_N32')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear3IncomeChange_N34"
                      @input="calculate('inputs.proformaYear3IncomeChange_N34')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear4IncomeChange_O34"
                      @input="calculate('inputs.proformaYear4IncomeChange_O34')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear5IncomeChange_P34"
                      @input="calculate('inputs.proformaYear5IncomeChange_P34')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
              </tr>

              <tr>
                <td class="col1">Expense Change
                  <b-tooltip
                    type="is-info"
                    label="The percentage increase of expenses over the prior year."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear2ExpenseChange_N33"
                      @input="calculate('inputs.proformaYear2ExpenseChange_N33')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear3ExpenseChange_N35"
                      @input="calculate('inputs.proformaYear3ExpenseChange_N35')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear4ExpenseChange_O35"
                      @input="calculate('inputs.proformaYear4ExpenseChange_O35')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformaYear5ExpenseChange_P35"
                      @input="calculate('inputs.proformaYear5ExpenseChange_P35')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td class="col1">Column Header
                  <b-tooltip
                    type="is-info"
                    label="The label to display for the header of each column on the evaluation."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>

                <td class="col2">
                  <input
                    type="text"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    v-model="$store.state.bpo2.inputs.actuals.label"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.actuals.label')"
                    :readonly="readonly"
                  />
                </td>

                <td class="col3">
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.brokers.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.brokers.label')"
                    :readonly="readonly"
                  />
                </td>

                <td class="col4 align-right column-header">
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.proformasYear1.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.proformasYear1.label')"
                    :readonly="readonly"
                  />
                </td>
                <td class="col5 align-right column-header">
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.proformasYear2.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.proformasYear2.label')"
                    size="is-small"
                    :readonly="readonly"
                  />
                </td>
                <td class="col6 align-right column-header">
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.proformasYear3.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.proformasYear3.label')"
                    :readonly="readonly"
                  />
                </td>
                <td
                  class="col7 align-right column-header"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.proformasYear4.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.proformasYear4.label')"
                    size="is-small"
                    :readonly="readonly"
                  />
                </td>
                <td
                  class="col8 align-right column-header"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >
                  <input
                    type="text"
                    v-model="$store.state.bpo2.inputs.proformasYear5.label"
                    class="input is-small"
                    style="background-color: #ffdd57 !important;"
                    @focus="$event.target.select()"
                    @input="calculate('inputs.showProformasYear4And5')"
                    size="is-small"
                    :readonly="readonly"
                  />
                </td>
              </tr>

              <tr>

                <td class="col1">Gross Rental Income
                  <b-tooltip
                    type="is-info"
                    label="The gross annual revenue from all of the rents for all of the units. Does not take into account vacancies and bad debt."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>

                <td class="col2">
                  <div class="align-right">{{ $store.state.bpo2.outputs.actuals.grossRentalIncome_J40 | currency(0) }}</div>
                  <div style="padding-top: 1em">
                    <span>
                      <b-radio
                        v-model="$store.state.bpo2.inputs.actuals.grossRentalIncomeCalcType"
                        native-value="Raw"
                        @input="calculate('inputs.actuals.grossRentalIncomeCalcType')"
                        :disabled="readonly"
                      >
                        Actual gross rental income
                      </b-radio>
                      <div class="level-right">
                        <vue-autonumeric
                          :disabled="$store.state.bpo2.inputs.actuals.grossRentalIncomeCalcType != 'Raw'"
                          class="input is-small"
                          style="text-align: right"
                          v-bind:style="[ ($store.state.bpo2.inputs.actuals.grossRentalIncomeCalcType != 'Raw') ? {} : { backgroundColor: inputBackgroundColor }]"
                          v-model="$store.state.bpo2.inputs.actuals.grossRentalIncome_J40"
                          @input="calculate('inputs.actuals.grossRentalIncome_J40')"
                          :options="currency"
                          :readonly="readonly"
                        />
                      </div>
                    </span>
                    <span>
                      <b-radio
                        v-model="$store.state.bpo2.inputs.actuals.grossRentalIncomeCalcType"
                        native-value="Calculated"
                        @input="calculate('inputs.actuals.grossRentalIncomeCalcType')"
                        :disabled="readonly"
                      >
                        Use Scheduled Rent
                      </b-radio>
                    </span>
                  </div>
                </td>

                <td class="col3">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.grossRentalIncome_K40"
                      @input="calculate('inputs.brokers.grossRentalIncome_K40')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td class="col4 align-right">{{ $store.state.bpo2.outputs.proformasYear1.grossRentalIncome_L40 | currency(0) }}</td>
                <td class="col5 align-right">{{ $store.state.bpo2.outputs.proformasYear2.grossRentalIncome_M40 | currency(0) }}</td>
                <td class="col6 align-right">{{ $store.state.bpo2.outputs.proformasYear3.grossRentalIncome_N40 | currency(0) }}</td>
                <td
                  class="col7 align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.grossRentalIncome_O40 | currency(0) }}</td>
                <td
                  class="col8 align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.grossRentalIncome_P40 | currency(0) }}</td>

              </tr>

              <tr>
                <td class="col1">Economic Vacancy</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.economicVacancy_J41 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.economicVacancy_K41 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.economicVacancy_L41 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.economicVacancy_M41 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.economicVacancy_N41 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.economicVacancy_O41 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.economicVacancy_P41 | currency(0) }}</td>
              </tr>

              <tr class="subline">

                <td style="padding-left: 2em">Economic Vacancy Rate
                  <b-tooltip
                    type="is-info"
                    label="The percentage of vacancy expected. It's recommended for the Actual column that you enter the Effective Rent Income (how much rent has been collected at the property in the last 12 months). The Economic Vacany Rate % is derived from this formula: annual effective rental income / annual scheduled rent income."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>

                <td>
                  <div class="align-right">{{ $store.state.bpo2.outputs.actuals.economicVacancyRatePercent_J42 | percent(1) }}</div>
                  <div style="padding-top: 1em">
                    <span>
                      <b-radio
                        v-model="$store.state.bpo2.inputs.actuals.economicVacancyRateCalcType"
                        native-value="Raw"
                        @input="calculate('inputs.actuals.economicVacancyRateCalcType')"
                        :disabled="readonly"
                      >
                        Actual economic vacancy rate
                      </b-radio>
                      <div class="level-right">
                        <vue-autonumeric
                          :disabled="$store.state.bpo2.inputs.actuals.economicVacancyRateCalcType != 'Raw'"
                          class="input is-small"
                          style="text-align: right"
                          v-bind:style="[ ($store.state.bpo2.inputs.actuals.economicVacancyRateCalcType != 'Raw') ? {} : { backgroundColor: inputBackgroundColor }]"
                          v-model="$store.state.bpo2.inputs.actuals.economicVacancyRate_J42"
                          @input="calculate('inputs.actuals.economicVacancyRate_J42')"
                          :options="percentage"
                          :readonly="readonly"
                        />
                      </div>
                    </span>
                    <span>
                      <b-radio
                        v-model="$store.state.bpo2.inputs.actuals.economicVacancyRateCalcType"
                        native-value="EffectiveRentalIncome"
                        @input="calculate('inputs.actuals.economicVacancyRateCalcType')"
                        :disabled="readonly"
                      >
                        Calculate based on Effective Rent Income
                      </b-radio>
                      <div class="level-right">
                        <vue-autonumeric
                          :disabled="$store.state.bpo2.inputs.actuals.economicVacancyRateCalcType != 'EffectiveRentalIncome'"
                          class="input is-small"
                          style="text-align: right"
                          v-bind:style="[ ($store.state.bpo2.inputs.actuals.economicVacancyRateCalcType != 'EffectiveRentalIncome') ? {} : { backgroundColor: inputBackgroundColor }]"
                          v-model="$store.state.bpo2.inputs.actuals.effectiveRentalIncome"
                          @input="calculate('inputs.actuals.effectiveRentalIncome')"
                          :options="currency"
                          :readonly="readonly"
                        />
                      </div>
                    </span>
                  </div>
                </td>

                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.economicVacancyRate_K42"
                      @input="calculate('inputs.brokers.economicVacancyRate_K42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.economicVacancyRate_L42"
                      @input="calculate('inputs.proformasYear1.economicVacancyRate_L42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear2.economicVacancyRate_M42"
                      @input="calculate('inputs.proformasYear2.economicVacancyRate_M42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear3.economicVacancyRate_N42"
                      @input="calculate('inputs.proformasYear3.economicVacancyRate_N42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear4.economicVacancyRate_O42"
                      @input="calculate('inputs.proformasYear4.economicVacancyRate_O42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear5.economicVacancyRate_P42"
                      @input="calculate('inputs.proformasYear5.economicVacancyRate_P42')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>

              </tr>

              <tr class="subtotal1">
                <td>Net Rental Income</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.netRentalIncome_J43 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netRentalIncome_K43 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netRentalIncome_L43 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netRentalIncome_M43 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netRentalIncome_N43 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netRentalIncome_O43 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netRentalIncome_P43 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Laundry
                  <b-tooltip
                    type="is-info"
                    label="Revenue generated by onsite laundry services, if the property has it."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.actuals.laundry_J44"
                      @input="calculate('inputs.actuals.laundry_J44')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.laundry_K44"
                      @input="calculate('inputs.brokers.laundry_K44')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.laundry_L44"
                      @input="calculate('inputs.proformasYear1.laundry_L44')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.laundry_M44 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.laundry_N44 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.laundry_O44 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.laundry_P44 | currency(0) }}</td>
              </tr>

              <tr>
                <td>RUBS
                  <b-tooltip
                    type="is-info"
                      label="(Ratio Utility Billing System) Revenue generated from charging tenants for utilities based on sqft and/or number of people living in unit."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.actuals.rubs"
                      @input="calculate('inputs.actuals.rubs')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.rubs"
                      @input="calculate('inputs.brokers.rubs')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.rubs"
                      @input="calculate('inputs.proformasYear1.rubs')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.rubs | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.rubs | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.rubs | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.rubs | currency(0) }}</td>
              </tr>

              <tr>
                <td>Misc. Other Income
                  <b-tooltip
                    type="is-info"
                    label="Revenue generated by sources other than rent and laundry."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.actuals.miscellaneousOtherIncome_J45"
                      @input="calculate('inputs.actuals.miscellaneousOtherIncome_J45')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.miscellaneousOtherIncome_K45"
                      @input="calculate('inputs.brokers.miscellaneousOtherIncome_K45')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.miscellaneousOtherIncome_L45"
                      @input="calculate('inputs.proformasYear1.miscellaneousOtherIncome_L45')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.miscellaneousOtherIncome_M45 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.miscellaneousOtherIncome_N45 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.miscellaneousOtherIncome_O45 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.miscellaneousOtherIncome_P45 | currency(0) }}</td>
              </tr>

              <tr class="subtotal1">
                <td>Total Net Income</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.totalNetIncome_J46 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.totalNetIncome_K46 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.totalNetIncome_L46 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.totalNetIncome_M46 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.totalNetIncome_N46 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.totalNetIncome_O46 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.totalNetIncome_P46 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Expenses per Unit
                  <b-tooltip
                    type="is-info"
                    label="Derived from operating expenses / # of units. Suggest starting at $3,000 per unit which will get a roof and basic paint job. We almost always use this number on a Quick Analysis before seeing the property."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.actuals.expensesPerUnit_J48"
                      @input="calculate('inputs.actuals.expensesPerUnit_J48')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.expensesPerUnit_K48"
                      @input="calculate('inputs.brokers.expensesPerUnit_K48')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.expensesPerUnit_L48 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.expensesPerUnit_M48 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.expensesPerUnit_N48 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.expensesPerUnit_O48 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.expensesPerUnit_P48 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Expenses per Sqft</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.expensesPerSqft_J49 | currency(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.expensesPerSqft_K49 | currency(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.expensesPerSqft_L49 | currency(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.expensesPerSqft_M49 | currency(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.expensesPerSqft_N49 | currency(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.expensesPerSqft_O49 | currency(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.expensesPerSqft_P49 | currency(2) }}</td>
              </tr>

              <tr>
                <td>Expenses as % of Income</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.expensesAsPercentOfIncome_J50 | percent(1) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.expensesAsPercentOfIncome_K50 | percent(1) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.expensesAsPercentOfIncome_L50 | percent(1) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.expensesAsPercentOfIncome_M50 | percent(1) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.expensesAsPercentOfIncome_N50 | percent(1) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.expensesAsPercentOfIncome_O50 | percent(1) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.expensesAsPercentOfIncome_P50 | percent(1) }}</td>
              </tr>

              <tr class="subtotal1">
                <td>Total Expenses</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.totalExpenses_J51 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.totalExpenses_K51 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.totalExpenses_L51 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.totalExpenses_M51 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.totalExpenses_N51 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.totalExpenses_O51 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.totalExpenses_P51 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal2">
                <td>Net Operating Income</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.netOperatingIncome_J53 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netOperatingIncome_K53 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netOperatingIncome_L53 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netOperatingIncome_M53 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netOperatingIncome_N53 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netOperatingIncome_O53 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netOperatingIncome_P53 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Mortgage Interest</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.mortgageInterest_J55 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.mortgageInterest_K55 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.mortgageInterest_L55 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.mortgageInterest_M55 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.mortgageInterest_N55 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.mortgageInterest_O55 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.mortgageInterest_P55 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Capital Reserves/Unit
                  <b-tooltip
                    type="is-info"
                    label="Funds set aside to cover future capital costs so they don't come out of cash flow."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.actuals.capitalReservesPerUnit_J57"
                      @input="calculate('inputs.actuals.capitalReservesPerUnit_J57')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.capitalReservesPerUnit_K57"
                      @input="calculate('inputs.brokers.capitalReservesPerUnit_K57')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.capitalReservesPerUnit_L57"
                      @input="calculate('inputs.proformasYear1.capitalReservesPerUnit_L57')"
                      :options="currency"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.capitalReservesPerUnit_M57 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.capitalReservesPerUnit_N57 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.capitalReservesPerUnit_O57 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.capitalReservesPerUnit_P57 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Capital Reserves</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.capitalReserves_J58 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.capitalReserves_K58 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.capitalReserves_L58 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.capitalReserves_M58 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.capitalReserves_N58 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.capitalReserves_O58 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.capitalReserves_P58 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal1">
                <td>Pre-tax Profit</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.preTaxProfit_J60 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.preTaxProfit_K60 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.preTaxProfit_L60 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.preTaxProfit_M60 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.preTaxProfit_N60 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.preTaxProfit_O60 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.preTaxProfit_P60 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Pre-tax ROI</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.preTaxROI_J61 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.preTaxROI_K61 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.preTaxROI_L61 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.preTaxROI_M61 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.preTaxROI_N61 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.preTaxROI_O61 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.preTaxROI_P61 | percent(2) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Asset Management
                  <b-tooltip
                    type="is-info"
                    label="Set to 0% if you are an independent owner. If you are a lead then the max fee per the Whitepaper you can charge for asset and property management is 5%. It is recommended to have 3% included above the ROI in the expenses, and 2% included here which will be below the ROI line."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                  <div
                    class="level-left"
                    style="margin-left: 4em"
                  >
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.assetManagementPercent_I63"
                      @input="calculate('inputs.assetManagementPercent_I63')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right"></td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.assetManagement_K63 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.assetManagement_L63 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.assetManagement_M63 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.assetManagement_N63 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.assetManagement_O63 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.assetManagement_P63 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Prinipal Reduction</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.principalReduction_J64 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.principalReduction_K64 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.principalReduction_L64 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.principalReduction_M64 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.principalReduction_N64 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.principalReduction_O64 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.principalReduction_P64 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Debt Service</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.debtService_J65 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.debtService_K65 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.debtService_L65 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.debtService_M65 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.debtService_N65 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.debtService_O65 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.debtService_P65 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Debt Service Coverage Ratio</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.debtServiceCoverageRatio_J66 | number(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.debtServiceCoverageRatio_K66 | number(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.debtServiceCoverageRatio_L66 | number(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.debtServiceCoverageRatio_M66 | number(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.debtServiceCoverageRatio_N66 | number(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.debtServiceCoverageRatio_O66 | number(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.debtServiceCoverageRatio_P66 | number(2) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal2">
                <td>Net Cash Flow</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.netCashflowAnnual_J68 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netCashflowAnnual_K68 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netCashflowAnnual_L68 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netCashflowAnnual_M68 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netCashflowAnnual_N68 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netCashflowAnnual_O68 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netCashflowAnnual_P68 | currency(0) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal2">
                <td>Net ROI (cash-on-cash)</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.netROICashOnCash_J70 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netROICashOnCash_K70 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netROICashOnCash_L70 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netROICashOnCash_M70 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netROICashOnCash_N70 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netROICashOnCash_O70 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netROICashOnCash_P70 | percent(2) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr>
                <td>Lead Investor Compensation
                  <b-tooltip
                    type="is-info"
                    label="If you are an independent owner, leave this 0%. For leads and per the Whitepaper, a 1st time lead can charge 5%, a 2nd time lead can charge 10%, and a 3rd time or more lead can charge 15%."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                  <div
                    class="level-left"
                    style="margin-left: 4em"
                  >
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.leadInvestorCompensationPercent_I72"
                      @input="calculate('inputs.leadInvestorCompensationPercent_I72')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td class="align-right"></td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.leadInvestorCompensation_K72 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.leadInvestorCompensation_L72 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.leadInvestorCompensation_M72 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.leadInvestorCompensation_N72 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.leadInvestorCompensation_O72 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.leadInvestorCompensation_P72 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Net to Passives</td>
                <td class="align-right"></td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netToPassives_K73 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netToPassives_L73 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netToPassives_M73 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netToPassives_N73 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netToPassives_O73 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netToPassives_P73 | currency(0) }}</td>
              </tr>

              <tr>
                <td>Net ROI to Passives</td>
                <td class="align-right"></td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.netROIToPassives_K74 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.netROIToPassives_L74 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.netROIToPassives_M74 | percent(2) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.netROIToPassives_N74 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.netROIToPassives_O74 | percent(2) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.netROIToPassives_P74 | percent(2) }}</td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal2">
                <td>Cap Rate / Cap Rate Projection
                  <b-tooltip
                    type="is-info"
                    label="Estimated rate of return for this investment. A Mentor or Broker will help you with this rate since it's market and asset class dependent."
                    multilined
                  >
                    <i class="far fa-question-circle fa-lg"></i>
                  </b-tooltip>
                </td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.capRateProjection_J76 | percent(2) }}</td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.brokers.capRateProjection_K76"
                      @input="calculate('inputs.brokers.capRateProjection_K76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear1.capRateProjection_L76"
                      @input="calculate('inputs.proformasYear1.capRateProjection_L76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear2.capRateProjection_M76"
                      @input="calculate('inputs.proformasYear2.capRateProjection_M76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td>
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear3.capRateProjection_N76"
                      @input="calculate('inputs.proformasYear3.capRateProjection_N76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear4.capRateProjection_O76"
                      @input="calculate('inputs.proformasYear4.capRateProjection_O76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5">
                  <div class="level-right">
                    <vue-autonumeric
                      class="input is-small"
                      style="text-align: right; width: 4em"
                      v-bind:style="{ backgroundColor: inputBackgroundColor }"
                      v-model="$store.state.bpo2.inputs.proformasYear5.capRateProjection_P76"
                      @input="calculate('inputs.proformasYear5.capRateProjection_P76')"
                      :options="percentage"
                      :readonly="readonly"
                    />
                  </div>
                </td>
              </tr>

              <tr>
                <td>&nbsp;</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
                <td v-if="$store.state.bpo2.inputs.showProformasYear4And5"></td>
              </tr>

              <tr class="subtotal3">
                <td>Summary Projection</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.actuals.brokerPriceOpinionProjection_J78 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.brokers.brokerPriceOpinionProjection_K78 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear1.brokerPriceOpinionProjection_L78 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear2.brokerPriceOpinionProjection_M78 | currency(0) }}</td>
                <td class="align-right">{{ $store.state.bpo2.outputs.proformasYear3.brokerPriceOpinionProjection_N78 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear4.brokerPriceOpinionProjection_O78 | currency(0) }}</td>
                <td
                  class="align-right"
                  v-if="$store.state.bpo2.inputs.showProformasYear4And5"
                >{{ $store.state.bpo2.outputs.proformasYear5.brokerPriceOpinionProjection_P78 | currency(0) }}</td>
              </tr>

            </table>

          </div>

        </div>

      </div>

    </div>

    <p class="disclaimer">DISCLAIMER: This represents an estimated sale price for this property. It is not the same as the opinion of value in an appraisal developed by a licensed appraiser under the Uniform Standards of Professional Appraisal Practice. LS Realty Advisors, Inc., and/or Lifestyles Realty, Inc., and/or Lifestyles Unlimited, Inc. have not verified the information and make no guarantee, warranty, or representation. This broker price opinion was either filled out by a Lifestyles representative or the potential buyer or other. Further investigation may be necessary as more information is made available. When more information is available the opinion may substantially change; the reader should be aware of the limited scope of this opinion.</p>

    <!-- <div
      class="card"
      ref="BrokerPriceOpinion"
    >
      <section class="hero is-info">
        <div class="hero-body">
          <div class="container">
            <h1 class="title">
              Price Opinion
            </h1>
          </div>
        </div>
      </section>
    </div>
    <div class="card">
      <div class="card-content">
        <Bpo2CalculatedResults />
      </div>
    </div> -->

    <b-modal
      :active.sync="$store.state.isRentCompsActive"
      has-modal-card
      :canCancel="[]"
    >
      <RentComps
        :propertyId="$route.params.property_id"
        :rents="$store.state.bpo2.outputs.rents"
        :inputsRents="$store.state.bpo2.inputs.rents"
      />
    </b-modal>

    <b-modal
      :active.sync="$store.state.isShareEvaluationActive"
      has-modal-card
      :canCancel="[]"
    >
      <ShareEvaluation />
    </b-modal>

  </div>

</template>

<script>
import Bpo2CalculatedResults from "@/components/properties/evaluations/multifamily/BPO2CalculatedResults.vue";
import Financial from "formulajs";
import Moment from "moment";
import { VueEditor } from "vue2-editor";
import { uuid } from "vue-uuid";
import Pusher from "pusher-js";
import ShareEvaluation from "@/components/properties/evaluations/multifamily/ShareEvaluation.vue";
import SharedHeader from "@/components/properties/evaluations/multifamily/SharedHeader.vue";
import VueAutonumeric from "vue-autonumeric/src/components/VueAutonumeric.vue";
import RentComps from "@/components/properties/evaluations/multifamily/RentComps.vue";

export default {
  components: {
    Bpo2CalculatedResults,
    Financial,
    Moment,
    VueEditor,
    uuid,
    Pusher,
    ShareEvaluation,
    SharedHeader,
    VueAutonumeric,
    RentComps
  },

  props: {
    property_id: undefined,
    bpo2_id: undefined,
    guid: undefined
  },

  async mounted() {
    if (this.$route.params.guid != undefined) {
      this.$store.state.isLoginRequired = false;
      this.$store.state.session = undefined;
      let guid = this.$route.params.guid;
      let editKey = this.$route.params.editKey
        ? this.$route.params.editKey
        : "";
      await this.createSharedSession(guid, editKey);
      if (this.$store.state.session) {
        this.$store.state.session.user.password = "";
        this.shared = true;
        this.$store.state.isLoginRequired = false;
        if (!editKey) this.readonly = true;
      } else return;
    }

    this.$store.dispatch("getNaaSurveys");
    var propertyId = this.$route.params.property_id;
    var bpo2Id = this.$route.params.bpo2_id;
    this.$store.dispatch("getProperty", propertyId);
    await this.$store.dispatch("getBPO2", { propertyId, bpo2Id });

    this.subscribe();

    if (!this.$store.state.bpo2.outputs.naaSurvey.id) {
      await this.updateMarket;
      await this.updateBpo2();
    }

    this.keyPressed = false;
  },

  created() {
    window.addEventListener(
      "keydown",
      async () => {
        await this.setKeyPressed();
      },
      true
    );
    window.addEventListener(
      "click",
      async () => {
        await this.setKeyPressed();
      },
      true
    );
  },

  beforeDestroy() {
    this.unsubscribe();
    if (this.shared) this.$store.state.session = undefined;
  },

  data() {
    return {
      inputBackgroundColor: "#FFDD57",
      showHelpDealInputs: false,
      rentModal: false,
      customToolbar: [["bold", "italic", "underline"]],
      okToPublish: true,
      pusher: [],
      instanceGuid: uuid.v1(),
      packets: [],
      shared: false,
      readonly: false,
      keyPressed: false,
      decimal: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: "",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 6,
        allowDecimalPadding: false
      },
      number: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: "",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 0
      },
      currency: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: "$",
        currencySymbolPlacement: "p",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 0
      },
      percentage: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: "%",
        currencySymbolPlacement: "s",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 6,
        allowDecimalPadding: false
      },
      years: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: " years",
        currencySymbolPlacement: "s",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 6,
        allowDecimalPadding: false
      },
      maxCurrency: {
        digitGroupSeparator: ",",
        decimalCharacter: ".",
        decimalCharacterAlternative: ".",
        currencySymbol: "$",
        currencySymbolPlacement: "p",
        suffixText: " max",
        roundingMethod: "U",
        minimumValue: "0",
        decimalPlaces: 0
      }
    };
  },

  methods: {
    showRentRangeComps() {
      this.$store.state.rentCompPropertyId = null;
      this.$store.state.rentComps = [];
      this.$store.state.isRentCompsActive = true;
    },

    shareEvaluation(evaluation) {
      this.$store.commit("UPDATE_EVALUATION", evaluation);
      this.$store.state.isShareEvaluationActive = true;
    },

    async setKeyPressed() {
      this.keyPressed = true;
    },

    async createSharedSession(guid, editKey) {
      await this.$store
        .dispatch("createSharedSession", { guid, editKey })
        .then(() => {}, () => {});
    },

    subscribe() {
      this.pusher = new Pusher("84c708ec29b0f3de7a16", {
        cluster: "mt1"
      });
      this.pusher.subscribe(this.$store.state.bpo2.id);
      this.pusher.bind("bpo2_field_update", data => {
        this.setFieldValue(
          data.bpo2,
          data.field,
          data.instanceGuid,
          data.timeStamp
        );
      });
    },

    unsubscribe() {
      this.pusher.unsubscribe(this.$store.state.bpo2.id);
    },

    setFieldValue(bpo2, field, instanceGuid, timeStamp) {
      if (this.instanceGuid != instanceGuid) {
        // Make sure this is the latest publish from this instanceGuid.
        if (this.isLatestPublish(instanceGuid, timeStamp)) {
          // Don't publish when this value gets updated!
          this.okToPublish = false;

          // Update the field to the value passed in the bpo.
          let code = "this.$store.state.bpo2." + field + " = bpo2." + field;
          eval(code);
        } else console.log("NOT UPDATED!");
      }
    },

    isLatestPublish(instanceGuid, timeStamp) {
      let latestPublish = this.packets.find(
        publish => publish.instanceGuid === instanceGuid
      );
      if (latestPublish == undefined) {
        // First packet we've received from this instance, so save it and return true.
        let packet = { instanceGuid, timeStamp };
        this.packets.push(packet);
        return true;
      } else {
        // If this timeStamp is older than the last one we got, don't do anything.
        if (timeStamp < latestPublish.timeStamp) return false;

        // This timeStamp is newer so replace it in the packets list and return true.
        this.packets.splice(
          this.packets.findIndex(item => item.instanceGuid === instanceGuid),
          1
        );

        let packet = { instanceGuid, timeStamp };
        this.packets.push(packet);
        return true;
      }
    },

    async calculate(field = "", delay = true) {
      let interval = 500;
      this.lastCalculateRequest = new Moment();

      if (this.okToPublish && this.keyPressed) {
        if (field) {
          // Publish the change.
          var propertyId = this.$route.params.property_id;
          var bpo2Id = this.$route.params.bpo2_id;
          var bpo2 = this.$store.state.bpo2;
          let instanceGuid = this.instanceGuid;
          let timeStamp = this.lastCalculateRequest.toISOString();
          this.$store
            .dispatch("bpo2PublishField", {
              propertyId,
              bpo2Id,
              bpo2,
              field,
              instanceGuid,
              timeStamp
            })
            .then(() => {})
            .catch(() => {});
        }
      } else {
        // It's now okay to publish again if more than 2 seconds have passed
        // since the page was mounted. This avoids calling Pusher for every
        // field on the page on the initial load.
        if (this.keyPressed == true) {
          this.okToPublish = true;
        }
      }

      // Queue up the calculation.
      if (delay) {
        setTimeout(() => {
          let now = new Moment();
          let lastCalc = this.lastCalculateRequest;
          let diff = now - lastCalc;
          if (diff >= interval) {
            this.doCalculations(this.$store.state.bpo2);

            // Presist to the document store.
            this.updateBpo2();
          }
        }, interval);
      } else this.doCalculations(this.$store.state.bpo2);
    },

    async updateBpo2() {
      var propertyId = this.$route.params.property_id;
      var bpo2Id = this.$route.params.bpo2_id;
      var bpo2 = this.$store.state.bpo2;
      await this.$store
        .dispatch("updateBpo2", {
          propertyId,
          bpo2Id,
          bpo2
        })
        .then(() => {})
        .catch(() => {});
    },

    getValue(input) {
      if (parseFloat(input)) return parseFloat(input);
      else return 0;
    },

    addRentLine() {
      let checkEmptyLines = this.$store.state.bpo2.inputs.rents.filter(
        line => line.unitCount === null
      );
      if (
        checkEmptyLines.length >= 1 &&
        this.$store.state.bpo2.inputs.rents.length > 0
      ) {
        this.calculate("inputs.rents");
        return;
      }
      this.$store.state.bpo2.inputs.rents.push({
        type: null,
        unitCount: null,
        sqft: null,
        currentRent: null,
        proformaRent: null
      });
      this.calculate("inputs.rents");
    },

    updateMarket() {
      var naaSurvey = this.$store.state.naaSurveys.find(
        s => s.marketId == this.$store.state.bpo2.inputs.naaSurveyMarketId
      );
      if (naaSurvey) {
        this.$store.state.bpo2.outputs.naaSurvey = naaSurvey;
      } else {
        naaSurvey = this.$store.state.naaSurveys.find(s => s.marketId == 12);
      }
      this.calculate("inputs.naaSurveyMarketId");
    },

    addOperatingExpensesLine(customList) {
      let checkEmptyLines = customList.filter(line => line.name === null);
      if (checkEmptyLines.length >= 1 && customList.length > 0) return;
      customList.push({
        name: null,
        amountActual: null,
        amountProforma: null
      });
      this.calculate("inputs.operatingExpenses.customList");
    },

    async removeRentLine(lineId) {
      await this.calculate("inputs.rents");
      if (!this.blockRemoval) {
        this.$store.state.bpo2.inputs.rents.splice(lineId, 1);
        await this.calculate("inputs.rents");
      }
    },

    removeLine(customList, lineId, field) {
      if (!this.blockRemoval) {
        customList.splice(lineId, 1);
      }
      this.calculate(field);
    },

    addLine(customList, field) {
      let checkEmptyLines = customList.filter(line => line.name === null);
      if (checkEmptyLines.length >= 1 && customList.length > 0) return;
      customList.push({
        name: null,
        amount: null
      });
      this.calculate(field);
    },

    updateRentIncreasePercent() {
      for (var rentRow of this.$store.state.bpo2.inputs.rents) {
        rentRow.proformaRent = this.updateProformaRent(rentRow.currentRent);
      }
      this.calculate("inputs.rents");
    },

    updateProformaRent(currentRent) {
      var currentRentInt = parseInt(currentRent);
      var increasePercent = parseFloat(
        this.$store.state.bpo2.inputs.proformasYear1.rentIncreasePercent_W26 /
          100
      );
      // Calculate the proformaRent.
      var ret = Math.round(currentRentInt + currentRentInt * increasePercent);
      this.calculate("inputs.rents");
      return ret;
    },

    goToProperty() {
      this.$router.push({
        name: "PropertyDetails",
        params: {
          property_id: this.$store.state.property.id
        }
      });
    },

    MortgageValue(inputs, outputs, isInterest, startMonth, endMonth) {
      if (outputs.loanAmount_J31 == 0) return 0;
      // Intermediate values.
      let monthlyInterestRate = inputs.interestRatePercent_J32 / 100 / 12;
      let numberOfPayments = Math.round(outputs.termInYears_J33 * 12, 0);
      let monthlyPayment =
        -1 *
        Financial.PMT(
          monthlyInterestRate,
          numberOfPayments - inputs.interestOnlyPeriodInYears_J34 * 12,
          outputs.loanAmount_J31,
          0,
          0
        );
      if (!isFinite(monthlyPayment)) monthlyPayment = 0;
      monthlyPayment = Math.round(monthlyPayment * 100) / 100;
      let balance = outputs.loanAmount_J31;
      let mortgageInterest = 0;
      let principalReduction = 0;
      for (let payNumber = 1; payNumber < 30 * 12; payNumber++) {
        // Calculate the interest.
        let interest = Math.round(monthlyInterestRate * balance * 100, 2) / 100;
        // Calculate the scheduled payment.
        let scheduledPayment = 0;
        if (payNumber <= inputs.interestOnlyPeriodInYears_J34 * 12)
          scheduledPayment = interest;
        else if (
          payNumber >
          Math.round((1 + monthlyInterestRate) * balance * 100, 2) / 100
        )
          scheduledPayment =
            Math.round((1 + monthlyInterestRate) * balance * 100, 2) / 100;
        else scheduledPayment = monthlyPayment;
        // Calculate the principal.
        let principal = scheduledPayment - interest;
        // Calculate the new balance.
        balance = balance - principal;
        if (payNumber >= startMonth && payNumber <= endMonth) {
          mortgageInterest += interest;
          principalReduction += principal;
        }
      }
      if (isInterest == 1) return mortgageInterest;
      else return principalReduction;
    },

    getOfferPriceTargetNetRoi(targetNetROI) {
      let high = 1000000000;
      let low = 0;
      let tolerance = 0.001;
      let top = targetNetROI + tolerance;
      let bottom = targetNetROI - tolerance;
      for (var index = 0; index < 100; index++) {
        let test = low + (high - low) / 2;

        // Make a copy of the bpo2.
        let bpo2 = JSON.parse(JSON.stringify(this.$store.state.bpo2));

        // Set the inputs.
        bpo2.inputs.offerPriceCalcType = "Raw";
        bpo2.inputs.offerPrice_J19 = test;

        // Do the calc and look at the result.
        this.doCalculations(bpo2);
        let netROI = bpo2.outputs.proformasYear1.netROICashOnCash_L70;

        // Adjust the inputs and try again if needed.
        if (netROI > top) low = test;
        else if (netROI < bottom || isNaN(netROI)) high = test;
        else return test;
      }

      return 0;
    },

    getOfferPriceTargetDSCR(targetDSCR) {
      let high = 1000000000;
      let low = 0;
      let tolerance = 0.001;
      let top = targetDSCR + tolerance;
      let bottom = targetDSCR - tolerance;
      for (var index = 0; index < 100; index++) {
        let test = low + (high - low) / 2;

        // Make a copy of the bpo2.
        let bpo2 = JSON.parse(JSON.stringify(this.$store.state.bpo2));

        // Set the inputs.
        bpo2.inputs.offerPriceCalcType = "Raw";
        bpo2.inputs.offerPrice_J19 = test;

        // Do the calc and look at the result.
        this.doCalculations(bpo2);
        let dscr = bpo2.outputs.proformasYear1.debtServiceCoverageRatio_L66;

        // Adjust the inputs and try again if needed.
        if (dscr > top) low = test;
        else if (dscr < bottom) high = test;
        else return test;
      }

      return 0;
    },

    getBrokerageFees() {
      let brokerageFee = this.$store.state.bpo2.outputs.closingCosts.closingCosts.find(
        e => e.name == "Brokerage Fees"
      );
      if (brokerageFee) return brokerageFee.amount;
      else return 0;
    },

    async doCalculations(bpo2) {
      let inputs = bpo2.inputs;
      let outputs = bpo2.outputs;

      // Do this 3 times so all the final outputs are used in the calcuations.
      for (var index = 0; index < 3; index++) {
        // Rent Grids.
        outputs.rents = [];
        inputs.rents.forEach(row => {
          if (!row.type) row.type = "";
          row.unitCount = parseInt(this.getValue(row.unitCount));
          row.sqft = parseInt(this.getValue(row.sqft));
          row.currentRent = parseInt(this.getValue(row.currentRent));
          row.proformaRent = parseInt(this.getValue(row.proformaRent));
          let newRow = {};
          newRow.type = row.type;
          newRow.unitCount = row.unitCount;
          newRow.sqft = row.sqft;
          newRow.currentRent = row.currentRent;
          newRow.currentPricePerSqft =
            row.sqft == 0 ? 0 : row.currentRent / row.sqft;
          newRow.currentMonthly = row.currentRent * row.unitCount;
          newRow.currentAnnual = row.currentRent * row.unitCount * 12;
          newRow.proformaRent = row.proformaRent;
          newRow.proformaPricePerSqft =
            row.sqft == 0 ? 0 : row.proformaRent / row.sqft;
          newRow.proformaMonthly = row.proformaRent * row.unitCount;
          newRow.proformaAnnual = row.proformaRent * row.unitCount * 12;
          outputs.rents.push(newRow);
        });
        outputs.totalUnitCount_R23 = outputs.rents
          .map(rent => rent.unitCount)
          .reduce((a, b) => a + b, 0);
        outputs.totalCurrentRent_S23 =
          outputs.totalUnitCount_R23 == 0
            ? 0
            : outputs.totalCurrentMonthly_V23 / outputs.totalUnitCount_R23;
        outputs.totalSqft_T23 = outputs.rents
          .map(rent => rent.sqft * rent.unitCount)
          .reduce((a, b) => a + b, 0);
        outputs.totalCurrentPricePerSqft_U23 =
          outputs.totalSqft_T23 == 0
            ? 0
            : outputs.totalCurrentMonthly_V23 / outputs.totalSqft_T23;
        outputs.totalCurrentMonthly_V23 = outputs.rents.reduce(
          (acc, currValue) => acc + currValue.currentMonthly,
          0
        );
        outputs.totalCurrentAnnual_W23 = outputs.rents.reduce(
          (acc, currValue) => acc + currValue.currentAnnual,
          0
        );
        outputs.totalProformaRent_S37 =
          outputs.totalUnitCount_R23 == 0
            ? 0
            : outputs.totalCurrentMonthly_V23 / outputs.totalUnitCount_R23;
        outputs.totalProformaPricePerSqft_U37 =
          outputs.totalSqft_T23 == 0
            ? 0
            : outputs.totalProformaMonthly_V37 / outputs.totalSqft_T23;
        outputs.totalProformaMonthly_V37 = outputs.rents.reduce(
          (acc, currValue) => acc + currValue.proformaMonthly,
          0
        );
        outputs.totalProformaAnnual_W37 = outputs.rents.reduce(
          (acc, currValue) => acc + currValue.proformaAnnual,
          0
        );
        let avgUnitSqft = outputs.rents
          .map(r => r.sqft * r.unitCount)
          .reduce((a, b) => a + b, 0);
        // Operating Expenses.
        outputs.operatingExpenses.operatingExpenses = [];
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Taxes",
          amountActual: parseFloat(inputs.operatingExpenses.taxesActual),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.taxesActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(inputs.operatingExpenses.taxesProforma),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.taxesProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.taxesPricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Salaries and Personnel",
          amountActual: parseFloat(
            inputs.operatingExpenses.salariesAndPersonnelActual
          ),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.salariesAndPersonnelActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.salariesAndPersonnelProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.salariesAndPersonnelProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.salariesAndPersonnelPricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Repair and Maintenance",
          amountActual: parseFloat(
            inputs.operatingExpenses.repairAndMaintenanceActual
          ),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.repairAndMaintenanceActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.repairAndMaintenanceProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.repairAndMaintenanceProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.repairAndMaintenancePricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Management Fees",
          amountActual: parseFloat(
            inputs.operatingExpenses.managementFeesActual
          ),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.managementFeesActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.managementFeesProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.managementFeesProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.managementFeesPricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Contract services",
          amountActual: parseFloat(
            inputs.operatingExpenses.contractServicesActual
          ),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.contractServicesActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.contractServicesProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.contractServicesProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.contractServicesPricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Utilities",
          amountActual: parseFloat(inputs.operatingExpenses.utilitiesActual),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.utilitiesActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.utilitiesProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.utilitiesProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.utilitiesPricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Administrative",
          amountActual: parseFloat(
            inputs.operatingExpenses.administrativeActual
          ),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.administrativeActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.administrativeProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.administrativeProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.adminitrativePricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Insurance",
          amountActual: parseFloat(inputs.operatingExpenses.insuranceActual),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.insuranceActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.insuranceProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.insuranceProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.insurancePricePerUnit
        });
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Marketing",
          amountActual: parseFloat(inputs.operatingExpenses.marketingActual),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.marketingActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.marketingProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.marketingProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: outputs.naaSurvey.marketingPricePerUnit
        });
        for (var row of inputs.operatingExpenses.customList) {
          if (!row.name) row.name = "";
          row.amountActual = parseFloat(this.getValue(row.amountActual));
          row.amountProforma = parseFloat(this.getValue(row.amountProforma));
          outputs.operatingExpenses.operatingExpenses.push({
            name: row.name,
            amountActual: row.amountActual,
            amountActualPerUnit:
              outputs.numberOfUnits_J28 == 0
                ? 0
                : row.amountActual / outputs.numberOfUnits_J28,
            amountProforma: row.amountProforma,
            amountProformaPerUnit:
              outputs.numberOfUnits_J28 == 0
                ? 0
                : row.amountProforma / outputs.numberOfUnits_J28,
            amountNaaPerUnit: 0
          });
        }
        outputs.operatingExpenses.operatingExpenses.push({
          name: "Deleted - Carried Over",
          amountActual: parseFloat(inputs.operatingExpenses.carriedOverActual),
          amountActualPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.carriedOverActual /
                outputs.numberOfUnits_J28,
          amountProforma: parseFloat(
            inputs.operatingExpenses.carriedOverProforma
          ),
          amountProformaPerUnit:
            outputs.numberOfUnits_J28 == 0
              ? 0
              : inputs.operatingExpenses.carriedOverProforma /
                outputs.numberOfUnits_J28,
          amountNaaPerUnit: 0
        });
        outputs.operatingExpenses.totalActual_S57 = outputs.operatingExpenses.operatingExpenses.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amountActual),
          0
        );
        outputs.operatingExpenses.totalActualPerUnit_T57 = outputs.operatingExpenses.operatingExpenses.reduce(
          (acc, currValue) =>
            acc + this.getValue(currValue.amountActualPerUnit),
          0
        );
        outputs.operatingExpenses.totalProforma_U57 = outputs.operatingExpenses.operatingExpenses.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amountProforma),
          0
        );
        outputs.operatingExpenses.totalProformaPerUnit_V57 = outputs.operatingExpenses.operatingExpenses.reduce(
          (acc, currValue) =>
            acc + this.getValue(currValue.amountProformaPerUnit),
          0
        );
        outputs.operatingExpenses.totalNaaSurveyPerUnit_W57 = outputs.operatingExpenses.operatingExpenses.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amountNaaPerUnit),
          0
        );
        // Capital Improvements.
        outputs.capitalImprovements.capitalImprovements = [];
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Siding",
          amount: parseFloat(inputs.capitalImprovements.siding)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Exterior Paint",
          amount: parseFloat(inputs.capitalImprovements.exteriorPaint)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "HVAC Replacement",
          amount: parseFloat(inputs.capitalImprovements.hvacReplacement)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Remodel Office",
          amount: parseFloat(inputs.capitalImprovements.remodelOffice)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Office Equipment",
          amount: parseFloat(inputs.capitalImprovements.officeEquipment)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Jet Sewers",
          amount: parseFloat(inputs.capitalImprovements.jetSewers)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Parking Lot",
          amount: parseFloat(inputs.capitalImprovements.parkingLot)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Upgrade Make-Readies",
          amount: parseFloat(inputs.capitalImprovements.upgradeMakeReadies)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Signage",
          amount: parseFloat(inputs.capitalImprovements.signage)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Trees",
          amount: parseFloat(inputs.capitalImprovements.trees)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Landscaping",
          amount: parseFloat(inputs.capitalImprovements.landscaping)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Boilers",
          amount: parseFloat(inputs.capitalImprovements.boilers)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Pools",
          amount: parseFloat(inputs.capitalImprovements.pools)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Roof",
          amount: parseFloat(inputs.capitalImprovements.roof)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Maintenance Equipment",
          amount: parseFloat(inputs.capitalImprovements.maintenanceEquipment)
        });
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Foundation",
          amount: parseFloat(inputs.capitalImprovements.foundation)
        });
        for (var row1 of inputs.capitalImprovements.customList) {
          if (!row1.name) row1.name = "";
          row1.amount = parseFloat(this.getValue(row1.amount));
          outputs.capitalImprovements.capitalImprovements.push({
            name: row1.name,
            amount: row1.amount
          });
        }
        outputs.capitalImprovements.capitalImprovements.push({
          name: "Delete - Carried Over",
          amount: parseFloat(inputs.capitalImprovements.carriedOver)
        });
        outputs.capitalImprovements.total_S89 = outputs.capitalImprovements.capitalImprovements.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amount),
          0
        );
        // Closing Costs.
        outputs.closingCosts.closingCosts = [];
        outputs.closingCosts.closingCosts.push({
          name: "Lender Fees",
          amount: parseFloat(inputs.closingCosts.lenderFees)
        });
        outputs.closingCosts.closingCosts.push({
          name: "Appraisal",
          amount: parseFloat(inputs.closingCosts.appraisal)
        });
        outputs.closingCosts.closingCosts.push({
          name: "Legal",
          amount: parseFloat(inputs.closingCosts.legal)
        });
        let brokerageFee = 0;
        if (inputs.closingCosts.brokerageFeesCalcType == "Raw")
          brokerageFee = inputs.closingCosts.brokerageFees;
        else if (inputs.closingCosts.brokerageFeesCalcType == "Calculated") {
          brokerageFee =
            (inputs.closingCosts.brokerageFeesPercentOfferPrice / 100) *
            outputs.offerPrice_J19;
        }
        outputs.closingCosts.closingCosts.push({
          name: "Brokerage Fees",
          amount: parseFloat(brokerageFee)
        });
        outputs.closingCosts.closingCosts.push({
          name: "Title Fees",
          amount: parseFloat(inputs.closingCosts.titleFees)
        });
        for (var row2 of inputs.closingCosts.customList) {
          if (!row2.name) row2.name = "";
          row2.amount = parseFloat(this.getValue(row2.amount));
          outputs.closingCosts.closingCosts.push({
            name: row2.name,
            amount: row2.amount
          });
        }
        outputs.closingCosts.closingCosts.push({
          name: "Delete - Carried Over",
          amount: parseFloat(inputs.closingCosts.carriedOver)
        });
        outputs.closingCosts.total_W68 = outputs.closingCosts.closingCosts.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amount),
          0
        );
        // Startup Costs.
        outputs.startupCosts.startupCosts = [];
        outputs.startupCosts.startupCosts.push({
          name: "Sewer Camera",
          amount: parseFloat(inputs.startupCosts.sewerCamera)
        });
        outputs.startupCosts.startupCosts.push({
          name: "Permits/Permit Checks",
          amount: parseFloat(inputs.startupCosts.permits)
        });
        outputs.startupCosts.startupCosts.push({
          name: "WDI Inspection",
          amount: parseFloat(inputs.startupCosts.wdiInspection)
        });
        outputs.startupCosts.startupCosts.push({
          name: "Survey",
          amount: parseFloat(inputs.startupCosts.survey)
        });
        outputs.startupCosts.startupCosts.push({
          name: "Environmental",
          amount: parseFloat(inputs.startupCosts.environmental)
        });
        for (var row3 of inputs.startupCosts.customList) {
          if (!row3.name) row3.name = "";
          row3.amount = parseFloat(this.getValue(row3.amount));
          outputs.startupCosts.startupCosts.push({
            name: row3.name,
            amount: row3.amount
          });
        }
        outputs.startupCosts.total_W77 = outputs.startupCosts.startupCosts.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amount),
          0
        );
        // Prepaids and Deposits.
        outputs.prepaidsAndDeposits.prepaidsAndDeposits = [];
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Insurance",
          amount: parseFloat(inputs.prepaidsAndDeposits.insurance)
        });
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Property Taxes",
          amount: parseFloat(inputs.prepaidsAndDeposits.propertyTaxes)
        });
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Water/Sewer Deposit",
          amount: parseFloat(inputs.prepaidsAndDeposits.waterSewerDeposit)
        });
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Electric Deposit",
          amount: parseFloat(inputs.prepaidsAndDeposits.electricDeposit)
        });
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Gas Deposit",
          amount: parseFloat(inputs.prepaidsAndDeposits.gasDeposit)
        });
        outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
          name: "Replacement Reserve",
          amount: parseFloat(inputs.prepaidsAndDeposits.replacementReserve)
        });
        for (var row4 of inputs.prepaidsAndDeposits.customList) {
          if (!row4.name) row4.name = "";
          row4.amount = parseFloat(this.getValue(row4.amount));
          outputs.prepaidsAndDeposits.prepaidsAndDeposits.push({
            name: row4.name,
            amount: row4.amount
          });
        }
        outputs.prepaidsAndDeposits.total_W89 = outputs.prepaidsAndDeposits.prepaidsAndDeposits.reduce(
          (acc, currValue) => acc + this.getValue(currValue.amount),
          0
        );
        // Outputs.
        outputs.askingPrice_J18 = parseInt(inputs.askingPrice_J18);
        outputs.askingPricePerUnit_K18 =
          outputs.numberOfUnits_J28 == 0
            ? 0
            : outputs.askingPrice_J18 / outputs.numberOfUnits_J28;
        if (inputs.offerPriceCalcType == "Raw")
          outputs.offerPrice_J19 = parseInt(inputs.offerPrice_J19);
        else if (inputs.offerPriceCalcType == "CapRate") {
          if (inputs.offerPriceCapRate != 0)
            outputs.offerPrice_J19 = parseInt(
              outputs.actuals.netOperatingIncome_J53 /
                (inputs.offerPriceCapRate / 100)
            );
          else outputs.offerPrice_J19 = 0;
        } else if (inputs.offerPriceCalcType == "Dscr")
          outputs.offerPrice_J19 = this.getOfferPriceTargetDSCR(
            inputs.offerPriceTargetDebtServiceCoverageRatio
          );
        else if (inputs.offerPriceCalcType == "NetRoi")
          outputs.offerPrice_J19 = this.getOfferPriceTargetNetRoi(
            inputs.offerPriceTargetNetReturnOnInvestment
          );
        else outputs.offerPrice_J19 = 0;
        outputs.offerPricePerUnit_K19 =
          outputs.numberOfUnits_J28 == 0
            ? 0
            : outputs.offerPrice_J19 / outputs.numberOfUnits_J28;
        outputs.rehabInLoan_J20 =
          outputs.capitalImprovements.total_S89 == 0
            ? 0
            : (outputs.rehabInLoan_J20 =
                (inputs.percentOfRehabToBeFinanced_J35 / 100) *
                outputs.capitalImprovements.total_S89);
        outputs.rehabInLoanPerUnit_K20 =
          outputs.numberOfUnits_J28 == 0
            ? 0
            : outputs.rehabInLoan_J20 / outputs.numberOfUnits_J28;
        outputs.totalIn_J21 = outputs.offerPrice_J19 + outputs.rehabInLoan_J20;
        outputs.closingAndStartupCostsPercent_I22 =
          outputs.totalIn_J21 == 0
            ? 0
            : (outputs.closingAndStartupCosts_J22 / outputs.totalIn_J21) * 100;
        outputs.closingAndStartupCosts_J22 =
          outputs.closingCosts.total_W68 + outputs.startupCosts.total_W77;
        if (inputs.operatingCapitalCalcType == "Raw")
          outputs.operatingCapital_J23 = parseFloat(
            inputs.operatingCapital_J23
          );
        else
          outputs.operatingCapital_J23 =
            (outputs.actuals.debtService_J65 +
              outputs.operatingExpenses.totalActual_S57 / 12) *
            2;
        outputs.rehabNotInLoan_J24 =
          outputs.capitalImprovements.total_S89 - outputs.rehabInLoan_J20;
        outputs.prepaidExpensesAndDeposits_J25 =
          outputs.prepaidsAndDeposits.total_W89;
        outputs.totalOutOfPocket_J26 =
          outputs.closingAndStartupCosts_J22 +
          outputs.operatingCapital_J23 +
          outputs.rehabNotInLoan_J24 +
          outputs.downPayment_K30 +
          outputs.prepaidExpensesAndDeposits_J25;
        outputs.numberOfUnits_J28 = outputs.rents.reduce(
          (a, r) => a + r.unitCount,
          0
        );
        outputs.totalInPricePerUnit_J29 =
          outputs.numberOfUnits_J28 == 0
            ? 0
            : outputs.totalIn_J21 / outputs.numberOfUnits_J28;
        outputs.downPaymentPercent_J30 = inputs.downPaymentPercent_J30;
        outputs.downPayment_K30 = Math.round(
          (outputs.downPaymentPercent_J30 / 100) * outputs.totalIn_J21
        );
        outputs.loanAmount_J31 = outputs.totalIn_J21 - outputs.downPayment_K30;
        outputs.interestRate_J32 = inputs.interestRatePercent_J32;
        outputs.termInYears_J33 = inputs.termInYears_J33;
        outputs.interestOnlyPeriodInYears_J34 =
          inputs.interestOnlyPeriodInYears_J34;
        outputs.percentOfRehabToBeFinanced_J35 =
          inputs.percentOfRehabToBeFinanced_J35;
        outputs.proformaYear2IncomeChange_N32 =
          inputs.proformaYear2IncomeChange_N32;
        outputs.proformaYear2ExpenseChange_N33 =
          inputs.proformaYear2ExpenseChange_N33;
        outputs.proformaYear3IncomeChange_N34 =
          inputs.proformaYear3IncomeChange_N34;
        outputs.proformaYear3ExpenseChange_N35 =
          inputs.proformaYear3ExpenseChange_N35;
        outputs.proformaYear4IncomeChange_O34 =
          inputs.proformaYear4IncomeChange_O34;
        outputs.proformaYear4ExpenseChange_O35 =
          inputs.proformaYear4ExpenseChange_O35;
        outputs.proformaYear5IncomeChange_P34 =
          inputs.proformaYear5IncomeChange_P34;
        outputs.proformaYear5ExpenseChange_P35 =
          inputs.proformaYear5ExpenseChange_P35;
        // Actuals.
        outputs.actuals.label_J38 = inputs.actuals.label;
        if (inputs.actuals.grossRentalIncomeCalcType == "Raw")
          outputs.actuals.grossRentalIncome_J40 =
            inputs.actuals.grossRentalIncome_J40;
        else
          outputs.actuals.grossRentalIncome_J40 =
            outputs.totalCurrentAnnual_W23;

        // outputs.actuals.economicVacancy_E39 =
        //   outputs.actuals.grossRentalIncome_E38 *
        //   (outputs.actuals.economicVacancyRate_K41 / 100);
        // if (inputs.actuals.economicVacanycRateCalcType == "Raw")
        //   outputs.actuals.economicVacancyRate_K41 = parseFloat(
        //     inputs.actuals.economicVacancyRate_K41
        //   );
        // else if (outputs.actuals.grossRentalIncome_E38 != 0)
        //   outputs.actuals.economicVacancyRate_K41 =
        //     (1 -
        //       inputs.actuals.effectiveRentalIncome /
        //         outputs.actuals.grossRentalIncome_E38) *
        //     100;

        outputs.actuals.economicVacancy_J41 =
          outputs.actuals.grossRentalIncome_J40 *
          (outputs.actuals.economicVacancyRatePercent_J42 / 100);
        if (inputs.actuals.economicVacancyRateCalcType == "Raw")
          outputs.actuals.economicVacancyRatePercent_J42 = parseFloat(
            inputs.actuals.economicVacancyRate_J42
          );
        else if (outputs.actuals.grossRentalIncome_J40 != 0)
          outputs.actuals.economicVacancyRatePercent_J42 =
            (1 -
              inputs.actuals.effectiveRentalIncome /
                outputs.actuals.grossRentalIncome_J40) *
            100;
        else outputs.actuals.economicVacancyRatePercent_J42 = 0;
        outputs.actuals.netRentalIncome_J43 =
          outputs.actuals.grossRentalIncome_J40 -
          outputs.actuals.economicVacancy_J41;
        outputs.actuals.laundry_J44 = inputs.actuals.laundry_J44;
        outputs.actuals.rubs = inputs.actuals.rubs;
        outputs.actuals.miscellaneousOtherIncome_J45 =
          inputs.actuals.miscellaneousOtherIncome_J45;
        outputs.actuals.totalNetIncome_J46 =
          outputs.actuals.netRentalIncome_J43 +
          outputs.actuals.laundry_J44 +
          outputs.actuals.rubs +
          outputs.actuals.miscellaneousOtherIncome_J45;
        outputs.actuals.expensesPerUnit_J48 =
          inputs.actuals.expensesPerUnit_J48;
        outputs.actuals.expensesPerSqft_J49 =
          avgUnitSqft == 0
            ? 0
            : outputs.actuals.totalExpenses_J51 / avgUnitSqft;
        outputs.actuals.expensesAsPercentOfIncome_J50 =
          outputs.actuals.totalNetIncome_J46 == 0
            ? 0
            : (outputs.actuals.totalExpenses_J51 /
                outputs.actuals.totalNetIncome_J46) *
              100;
        outputs.actuals.totalExpenses_J51 =
          outputs.numberOfUnits_J28 * outputs.actuals.expensesPerUnit_J48;
        outputs.actuals.netOperatingIncome_J53 =
          outputs.actuals.totalNetIncome_J46 -
          outputs.actuals.totalExpenses_J51;
        outputs.actuals.mortgageInterest_J55 =
          outputs.loanAmount_J31 == 0 || outputs.termInYears_J33 == 0
            ? 0
            : Financial.CUMIPMT(
                outputs.interestRate_J32 / 100 / 12,
                outputs.termInYears_J33 * 12,
                outputs.loanAmount_J31,
                1,
                12,
                0
              ) * -1;
        outputs.actuals.capitalReservesPerUnit_J57 =
          inputs.actuals.capitalReservesPerUnit_J57;
        outputs.actuals.capitalReserves_J58 =
          outputs.numberOfUnits_J28 *
          outputs.actuals.capitalReservesPerUnit_J57;
        outputs.actuals.preTaxProfit_J60 =
          outputs.actuals.netOperatingIncome_J53 -
          outputs.actuals.mortgageInterest_J55 -
          outputs.actuals.capitalReserves_J58;
        outputs.actuals.preTaxROI_J61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.actuals.preTaxProfit_J60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.assetManagementPercent_I63 = inputs.assetManagementPercent_I63;
        outputs.actuals.principalReduction_J64 =
          outputs.loanAmount_J31 == 0 || outputs.termInYears_J33 == 0
            ? 0
            : Financial.CUMPRINC(
                outputs.interestRate_J32 / 100 / 12,
                outputs.termInYears_J33 * 12,
                outputs.loanAmount_J31,
                1,
                12,
                0
              ) * -1;
        outputs.actuals.debtService_J65 =
          outputs.actuals.mortgageInterest_J55 +
          outputs.actuals.principalReduction_J64;
        outputs.actuals.debtServiceCoverageRatio_J66 =
          outputs.actuals.debtService_J65 == 0
            ? 0
            : (outputs.actuals.netOperatingIncome_J53 -
                outputs.actuals.capitalReserves_J58) /
              outputs.actuals.debtService_J65;
        outputs.actuals.netCashflowAnnual_J68 =
          outputs.actuals.preTaxProfit_J60 -
          outputs.actuals.principalReduction_J64;
        outputs.actuals.netROICashOnCash_J70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.actuals.netCashflowAnnual_J68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.leadInvestorCompensationPercent_I72 =
          inputs.leadInvestorCompensationPercent_I72;
        outputs.actuals.capRateProjection_J76 =
          outputs.offerPrice_J19 == 0
            ? 0
            : (outputs.actuals.netOperatingIncome_J53 /
                outputs.offerPrice_J19) *
              100;
        outputs.actuals.brokerPriceOpinionProjection_J78 =
          outputs.actuals.capRateProjection_J76 == 0
            ? 0
            : outputs.actuals.netOperatingIncome_J53 /
              (outputs.actuals.capRateProjection_J76 / 100);
        // Brokers.
        outputs.brokers.label_K38 = inputs.brokers.label;
        outputs.brokers.grossRentalIncome_K40 =
          inputs.brokers.grossRentalIncome_K40;
        outputs.brokers.economicVacancy_K41 =
          inputs.brokers.grossRentalIncome_K40 *
          (inputs.brokers.economicVacancyRate_K42 / 100);
        outputs.brokers.economicVacancyRatePercent_K42 =
          inputs.brokers.economicVacancyRate_K42;
        outputs.brokers.netRentalIncome_K43 =
          inputs.brokers.grossRentalIncome_K40 -
          outputs.brokers.economicVacancy_K41;
        outputs.brokers.laundry_K44 = inputs.brokers.laundry_K44;
        outputs.brokers.rubs = inputs.brokers.rubs;
        outputs.brokers.miscellaneousOtherIncome_K45 =
          inputs.brokers.miscellaneousOtherIncome_K45;
        outputs.brokers.totalNetIncome_K46 =
          outputs.brokers.netRentalIncome_K43 +
          outputs.brokers.laundry_K44 +
          outputs.brokers.rubs +
          outputs.brokers.miscellaneousOtherIncome_K45;
        outputs.brokers.expensesPerUnit_K48 =
          inputs.brokers.expensesPerUnit_K48;
        outputs.brokers.expensesPerSqft_K49 =
          avgUnitSqft == 0
            ? 0
            : outputs.brokers.totalExpenses_K51 / avgUnitSqft;
        outputs.brokers.expensesAsPercentOfIncome_K50 =
          outputs.brokers.totalNetIncome_K46 == 0
            ? 0
            : (outputs.brokers.totalExpenses_K51 /
                outputs.brokers.totalNetIncome_K46) *
              100;
        outputs.brokers.totalExpenses_K51 =
          outputs.numberOfUnits_J28 * outputs.brokers.expensesPerUnit_K48;
        outputs.brokers.netOperatingIncome_K53 =
          outputs.brokers.totalNetIncome_K46 -
          outputs.brokers.totalExpenses_K51;
        outputs.brokers.mortgageInterest_K55 =
          outputs.actuals.mortgageInterest_J55;
        outputs.brokers.capitalReservesPerUnit_K57 =
          inputs.brokers.capitalReservesPerUnit_K57;
        outputs.brokers.capitalReserves_K58 =
          outputs.numberOfUnits_J28 *
          outputs.brokers.capitalReservesPerUnit_K57;
        outputs.brokers.preTaxProfit_K60 =
          outputs.brokers.netOperatingIncome_K53 -
          outputs.brokers.mortgageInterest_K55 -
          outputs.brokers.capitalReserves_K58;
        outputs.brokers.preTaxROI_K61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.brokers.preTaxProfit_K60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.brokers.assetManagement_K63 =
          outputs.brokers.totalNetIncome_K46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.brokers.principalReduction_K64 =
          outputs.loanAmount_J31 == 0 || outputs.termInYears_J33 == 0
            ? 0
            : Financial.CUMPRINC(
                outputs.interestRate_J32 / 100 / 12,
                outputs.termInYears_J33 * 12,
                outputs.loanAmount_J31,
                1,
                12,
                0
              ) * -1;
        outputs.brokers.debtService_K65 =
          outputs.brokers.mortgageInterest_K55 +
          outputs.brokers.principalReduction_K64;
        outputs.brokers.debtServiceCoverageRatio_K66 =
          outputs.brokers.debtService_K65 == 0
            ? 0
            : (outputs.brokers.netOperatingIncome_K53 -
                outputs.brokers.capitalReserves_K58) /
              outputs.brokers.debtService_K65;
        outputs.brokers.netCashflowAnnual_K68 =
          outputs.brokers.preTaxProfit_K60 -
          outputs.brokers.principalReduction_K64 -
          outputs.brokers.assetManagement_K63;
        outputs.brokers.netROICashOnCash_K70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.brokers.netCashflowAnnual_K68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.brokers.leadInvestorCompensation_K72 =
          outputs.brokers.netCashflowAnnual_K68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.brokers.netToPassives_K73 =
          outputs.brokers.netCashflowAnnual_K68 -
          outputs.brokers.leadInvestorCompensation_K72;
        outputs.brokers.netROIToPassives_K74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.brokers.netToPassives_K73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.brokers.capRateProjection_K76 =
          inputs.brokers.capRateProjection_K76;
        outputs.brokers.brokerPriceOpinionProjection_K78 =
          outputs.brokers.capRateProjection_K76 == 0
            ? 0
            : outputs.brokers.netOperatingIncome_K53 /
              (outputs.brokers.capRateProjection_K76 / 100);
        // Proforma Year 1.
        outputs.proformasYear1.label_L38 = inputs.proformasYear1.label;
        outputs.proformasYear1.grossRentalIncome_L40 = outputs.rents.reduce(
          (a, r) => a + r.proformaAnnual,
          0
        );
        outputs.proformasYear1.economicVacancy_L41 =
          outputs.proformasYear1.grossRentalIncome_L40 *
          (inputs.proformasYear1.economicVacancyRate_L42 / 100);
        outputs.proformasYear1.economicVacancyRatePercent_L42 =
          inputs.proformasYear1.economicVacancyRate_L42;
        outputs.proformasYear1.netRentalIncome_L43 =
          outputs.proformasYear1.grossRentalIncome_L40 -
          outputs.proformasYear1.economicVacancy_L41;
        outputs.proformasYear1.laundry_L44 = inputs.proformasYear1.laundry_L44;
        outputs.proformasYear1.rubs = inputs.proformasYear1.rubs;
        outputs.proformasYear1.miscellaneousOtherIncome_L45 =
          inputs.proformasYear1.miscellaneousOtherIncome_L45;
        outputs.proformasYear1.totalNetIncome_L46 =
          outputs.proformasYear1.netRentalIncome_L43 +
          outputs.proformasYear1.laundry_L44 +
          outputs.proformasYear1.rubs +
          outputs.proformasYear1.miscellaneousOtherIncome_L45;
        outputs.proformasYear1.expensesPerUnit_L48 =
          outputs.numberOfUnits_J28 == 0
            ? 0
            : outputs.operatingExpenses.totalProforma_U57 /
              outputs.numberOfUnits_J28;
        outputs.proformasYear1.expensesPerSqft_L49 =
          avgUnitSqft == 0
            ? 0
            : outputs.proformasYear1.totalExpenses_L51 / avgUnitSqft;
        outputs.proformasYear1.expensesAsPercentOfIncome_L50 =
          outputs.proformasYear1.totalNetIncome_L46 == 0
            ? 0
            : (outputs.proformasYear1.totalExpenses_L51 /
                outputs.proformasYear1.totalNetIncome_L46) *
              100;
        outputs.proformasYear1.totalExpenses_L51 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear1.expensesPerUnit_L48;
        outputs.proformasYear1.netOperatingIncome_L53 =
          outputs.proformasYear1.totalNetIncome_L46 -
          outputs.proformasYear1.totalExpenses_L51;
        outputs.proformasYear1.mortgageInterest_L55 = this.MortgageValue(
          inputs,
          outputs,
          1,
          1,
          12
        );
        outputs.proformasYear1.capitalReservesPerUnit_L57 =
          inputs.proformasYear1.capitalReservesPerUnit_L57;
        outputs.proformasYear1.capitalReserves_L58 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear1.capitalReservesPerUnit_L57;
        outputs.proformasYear1.preTaxProfit_L60 =
          outputs.proformasYear1.netOperatingIncome_L53 -
          outputs.proformasYear1.mortgageInterest_L55 -
          outputs.proformasYear1.capitalReserves_L58;
        outputs.proformasYear1.preTaxROI_L61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear1.preTaxProfit_L60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear1.assetManagement_L63 =
          outputs.proformasYear1.totalNetIncome_L46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.proformasYear1.principalReduction_L64 = this.MortgageValue(
          inputs,
          outputs,
          0,
          1,
          12
        );
        outputs.proformasYear1.debtService_L65 =
          outputs.proformasYear1.mortgageInterest_L55 +
          outputs.proformasYear1.principalReduction_L64;
        outputs.proformasYear1.debtServiceCoverageRatio_L66 =
          outputs.proformasYear1.debtService_L65 == 0
            ? 0
            : (outputs.proformasYear1.netOperatingIncome_L53 -
                outputs.proformasYear1.capitalReserves_L58) /
              outputs.proformasYear1.debtService_L65;
        outputs.proformasYear1.netCashflowAnnual_L68 =
          outputs.proformasYear1.preTaxProfit_L60 -
          outputs.proformasYear1.principalReduction_L64 -
          outputs.proformasYear1.assetManagement_L63;
        outputs.proformasYear1.netROICashOnCash_L70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear1.netCashflowAnnual_L68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear1.leadInvestorCompensation_L72 =
          outputs.proformasYear1.netCashflowAnnual_L68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.proformasYear1.netToPassives_L73 =
          outputs.proformasYear1.netCashflowAnnual_L68 -
          outputs.proformasYear1.leadInvestorCompensation_L72;
        outputs.proformasYear1.netROIToPassives_L74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear1.netToPassives_L73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear1.capRateProjection_L76 =
          inputs.proformasYear1.capRateProjection_L76;
        outputs.proformasYear1.brokerPriceOpinionProjection_L78 =
          outputs.proformasYear1.capRateProjection_L76 == 0
            ? 0
            : outputs.proformasYear1.netOperatingIncome_L53 /
              (outputs.proformasYear1.capRateProjection_L76 / 100);
        // Proforma Year 2.
        outputs.proformasYear2.label_M38 = inputs.proformasYear2.label;
        outputs.proformasYear2.grossRentalIncome_M40 =
          outputs.proformasYear1.grossRentalIncome_L40 *
          (1 + inputs.proformaYear2IncomeChange_N32 / 100);
        outputs.proformasYear2.economicVacancy_M41 =
          outputs.proformasYear2.grossRentalIncome_M40 *
          (inputs.proformasYear2.economicVacancyRate_M42 / 100);
        outputs.proformasYear2.economicVacancyRatePercent_M42 =
          inputs.proformasYear2.economicVacancyRate_M42;
        outputs.proformasYear2.netRentalIncome_M43 =
          outputs.proformasYear2.grossRentalIncome_M40 -
          outputs.proformasYear2.economicVacancy_M41;
        outputs.proformasYear2.laundry_M44 =
          outputs.proformasYear1.laundry_L44 *
          (1 + inputs.proformaYear2IncomeChange_N32 / 100);
        outputs.proformasYear2.rubs =
          outputs.proformasYear1.rubs *
          (1 + inputs.proformaYear2IncomeChange_N32 / 100);
        outputs.proformasYear2.miscellaneousOtherIncome_M45 =
          outputs.proformasYear1.miscellaneousOtherIncome_L45 *
          (1 + inputs.proformaYear2IncomeChange_N32 / 100);
        outputs.proformasYear2.totalNetIncome_M46 =
          outputs.proformasYear2.netRentalIncome_M43 +
          outputs.proformasYear2.laundry_M44 +
          outputs.proformasYear2.rubs +
          outputs.proformasYear2.miscellaneousOtherIncome_M45;
        outputs.proformasYear2.expensesPerUnit_M48 =
          outputs.proformasYear1.expensesPerUnit_L48 *
          (1 + inputs.proformaYear2ExpenseChange_N33 / 100);
        outputs.proformasYear2.expensesPerSqft_M49 =
          avgUnitSqft == 0
            ? 0
            : outputs.proformasYear2.totalExpenses_M51 / avgUnitSqft;
        outputs.proformasYear2.expensesAsPercentOfIncome_M50 =
          outputs.proformasYear2.totalNetIncome_M46 == 0
            ? 0
            : (outputs.proformasYear2.totalExpenses_M51 /
                outputs.proformasYear2.totalNetIncome_M46) *
              100;
        outputs.proformasYear2.totalExpenses_M51 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear2.expensesPerUnit_M48;
        outputs.proformasYear2.netOperatingIncome_M53 =
          outputs.proformasYear2.totalNetIncome_M46 -
          outputs.proformasYear2.totalExpenses_M51;
        outputs.proformasYear2.mortgageInterest_M55 = this.MortgageValue(
          inputs,
          outputs,
          1,
          13,
          24
        );
        outputs.proformasYear2.capitalReservesPerUnit_M57 =
          outputs.proformasYear1.capitalReservesPerUnit_L57 *
          (1 + inputs.proformaYear2ExpenseChange_N33 / 100);
        outputs.proformasYear2.capitalReserves_M58 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear2.capitalReservesPerUnit_M57;
        outputs.proformasYear2.preTaxProfit_M60 =
          outputs.proformasYear2.netOperatingIncome_M53 -
          outputs.proformasYear2.mortgageInterest_M55 -
          outputs.proformasYear2.capitalReserves_M58;
        outputs.proformasYear2.preTaxROI_M61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear2.preTaxProfit_M60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear2.assetManagement_M63 =
          outputs.proformasYear2.totalNetIncome_M46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.proformasYear2.principalReduction_M64 = this.MortgageValue(
          inputs,
          outputs,
          0,
          13,
          24
        );
        outputs.proformasYear2.debtService_M65 =
          outputs.proformasYear2.mortgageInterest_M55 +
          outputs.proformasYear2.principalReduction_M64;
        outputs.proformasYear2.debtServiceCoverageRatio_M66 =
          outputs.proformasYear2.debtService_M65 == 0
            ? 0
            : (outputs.proformasYear2.netOperatingIncome_M53 -
                outputs.proformasYear2.capitalReserves_M58) /
              outputs.proformasYear2.debtService_M65;
        outputs.proformasYear2.netCashflowAnnual_M68 =
          outputs.proformasYear2.preTaxProfit_M60 -
          outputs.proformasYear2.principalReduction_M64 -
          outputs.proformasYear2.assetManagement_M63;
        outputs.proformasYear2.netROICashOnCash_M70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear2.netCashflowAnnual_M68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear2.leadInvestorCompensation_M72 =
          outputs.proformasYear2.netCashflowAnnual_M68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.proformasYear2.netToPassives_M73 =
          outputs.proformasYear2.netCashflowAnnual_M68 -
          outputs.proformasYear2.leadInvestorCompensation_M72;
        outputs.proformasYear2.netROIToPassives_M74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear2.netToPassives_M73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear2.capRateProjection_M76 =
          inputs.proformasYear2.capRateProjection_M76;
        outputs.proformasYear2.brokerPriceOpinionProjection_M78 =
          outputs.proformasYear2.capRateProjection_M76 == 0
            ? 0
            : outputs.proformasYear2.netOperatingIncome_M53 /
              (outputs.proformasYear2.capRateProjection_M76 / 100);
        // Proforma Year 3.
        outputs.proformasYear3.label_N38 = inputs.proformasYear3.label;
        outputs.proformasYear3.grossRentalIncome_N40 =
          outputs.proformasYear2.grossRentalIncome_M40 *
          (1 + inputs.proformaYear3IncomeChange_N34 / 100);
        outputs.proformasYear3.economicVacancy_N41 =
          outputs.proformasYear3.grossRentalIncome_N40 *
          (inputs.proformasYear3.economicVacancyRate_N42 / 100);
        outputs.proformasYear3.economicVacancyRatePercent_N42 =
          inputs.proformasYear3.economicVacancyRate_N42;
        outputs.proformasYear3.netRentalIncome_N43 =
          outputs.proformasYear3.grossRentalIncome_N40 -
          outputs.proformasYear3.economicVacancy_N41;
        outputs.proformasYear3.laundry_N44 =
          outputs.proformasYear2.laundry_M44 *
          (1 + inputs.proformaYear3IncomeChange_N34 / 100);
        outputs.proformasYear3.rubs =
          outputs.proformasYear2.rubs *
          (1 + inputs.proformaYear3IncomeChange_N34 / 100);
        outputs.proformasYear3.miscellaneousOtherIncome_N45 =
          outputs.proformasYear2.miscellaneousOtherIncome_M45 *
          (1 + inputs.proformaYear3IncomeChange_N34 / 100);
        outputs.proformasYear3.totalNetIncome_N46 =
          outputs.proformasYear3.netRentalIncome_N43 +
          outputs.proformasYear3.laundry_N44 +
          outputs.proformasYear3.rubs +
          outputs.proformasYear3.miscellaneousOtherIncome_N45;
        outputs.proformasYear3.expensesPerUnit_N48 =
          outputs.proformasYear2.expensesPerUnit_M48 *
          (1 + inputs.proformaYear3ExpenseChange_N35 / 100);
        outputs.proformasYear3.expensesPerSqft_N49 =
          avgUnitSqft == 0
            ? 0
            : outputs.proformasYear3.totalExpenses_N51 / avgUnitSqft;
        outputs.proformasYear3.expensesAsPercentOfIncome_N50 =
          outputs.proformasYear3.totalNetIncome_N46 == 0
            ? 0
            : (outputs.proformasYear3.totalExpenses_N51 /
                outputs.proformasYear3.totalNetIncome_N46) *
              100;
        outputs.proformasYear3.totalExpenses_N51 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear3.expensesPerUnit_N48;
        outputs.proformasYear3.netOperatingIncome_N53 =
          outputs.proformasYear3.totalNetIncome_N46 -
          outputs.proformasYear3.totalExpenses_N51;
        outputs.proformasYear3.mortgageInterest_N55 = this.MortgageValue(
          inputs,
          outputs,
          1,
          25,
          36
        );
        outputs.proformasYear3.capitalReservesPerUnit_N57 =
          outputs.proformasYear2.capitalReservesPerUnit_M57 *
          (1 + inputs.proformaYear3ExpenseChange_N35 / 100);
        outputs.proformasYear3.capitalReserves_N58 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear3.capitalReservesPerUnit_N57;
        outputs.proformasYear3.preTaxProfit_N60 =
          outputs.proformasYear3.netOperatingIncome_N53 -
          outputs.proformasYear3.mortgageInterest_N55 -
          outputs.proformasYear3.capitalReserves_N58;
        outputs.proformasYear3.preTaxROI_N61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear3.preTaxProfit_N60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear3.assetManagement_N63 =
          outputs.proformasYear3.totalNetIncome_N46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.proformasYear3.principalReduction_N64 = this.MortgageValue(
          inputs,
          outputs,
          0,
          25,
          36
        );
        outputs.proformasYear3.debtService_N65 =
          outputs.proformasYear3.mortgageInterest_N55 +
          outputs.proformasYear3.principalReduction_N64;
        outputs.proformasYear3.debtServiceCoverageRatio_N66 =
          outputs.proformasYear3.debtService_N65 == 0
            ? 0
            : (outputs.proformasYear3.netOperatingIncome_N53 -
                outputs.proformasYear3.capitalReserves_N58) /
              outputs.proformasYear3.debtService_N65;
        outputs.proformasYear3.netCashflowAnnual_N68 =
          outputs.proformasYear3.preTaxProfit_N60 -
          outputs.proformasYear3.principalReduction_N64 -
          outputs.proformasYear3.assetManagement_N63;
        outputs.proformasYear3.netROICashOnCash_N70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear3.netCashflowAnnual_N68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear3.leadInvestorCompensation_N72 =
          outputs.proformasYear3.netCashflowAnnual_N68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.proformasYear3.netToPassives_N73 =
          outputs.proformasYear3.netCashflowAnnual_N68 -
          outputs.proformasYear3.leadInvestorCompensation_N72;
        outputs.proformasYear3.netROIToPassives_N74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear3.netToPassives_N73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear3.capRateProjection_N76 =
          inputs.proformasYear3.capRateProjection_N76;
        outputs.proformasYear3.brokerPriceOpinionProjection_N78 =
          outputs.proformasYear3.capRateProjection_N76 == 0
            ? 0
            : outputs.proformasYear3.netOperatingIncome_N53 /
              (outputs.proformasYear3.capRateProjection_N76 / 100);
        // Proforma Year 4.
        outputs.proformasYear4.label_O38 = inputs.proformasYear4.label;
        outputs.proformasYear4.grossRentalIncome_O40 =
          outputs.proformasYear3.grossRentalIncome_N40 *
          (1 + inputs.proformaYear4IncomeChange_O34 / 100);
        outputs.proformasYear4.economicVacancy_O41 =
          outputs.proformasYear4.grossRentalIncome_O40 *
          (inputs.proformasYear4.economicVacancyRate_O42 / 100);
        outputs.proformasYear4.economicVacancyRatePercent_O42 =
          inputs.proformasYear4.economicVacancyRate_O42;
        outputs.proformasYear4.netRentalIncome_O43 =
          outputs.proformasYear4.grossRentalIncome_O40 -
          outputs.proformasYear4.economicVacancy_O41;
        outputs.proformasYear4.laundry_O44 =
          outputs.proformasYear3.laundry_N44 *
          (1 + inputs.proformaYear4IncomeChange_O34 / 100);
        outputs.proformasYear4.rubs =
          outputs.proformasYear3.rubs *
          (1 + inputs.proformaYear4IncomeChange_O34 / 100);
        outputs.proformasYear4.miscellaneousOtherIncome_O45 =
          outputs.proformasYear3.miscellaneousOtherIncome_N45 *
          (1 + inputs.proformaYear4IncomeChange_O34 / 100);
        outputs.proformasYear4.totalNetIncome_O46 =
          outputs.proformasYear4.netRentalIncome_O43 +
          outputs.proformasYear4.laundry_O44 +
          outputs.proformasYear4.rubs +
          outputs.proformasYear4.miscellaneousOtherIncome_O45;
        outputs.proformasYear4.expensesPerUnit_O48 =
          outputs.proformasYear3.expensesPerUnit_N48 *
          (1 + inputs.proformaYear4ExpenseChange_O35 / 100);
        outputs.proformasYear4.expensesPerSqft_O49 =
          avgUnitSqft == 0
            ? 0
            : outputs.proformasYear4.totalExpenses_O51 / avgUnitSqft;
        outputs.proformasYear4.expensesAsPercentOfIncome_O50 =
          outputs.proformasYear4.totalNetIncome_O46 == 0
            ? 0
            : (outputs.proformasYear4.totalExpenses_O51 /
                outputs.proformasYear4.totalNetIncome_O46) *
              100;
        outputs.proformasYear4.totalExpenses_O51 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear4.expensesPerUnit_O48;
        outputs.proformasYear4.netOperatingIncome_O53 =
          outputs.proformasYear4.totalNetIncome_O46 -
          outputs.proformasYear4.totalExpenses_O51;
        outputs.proformasYear4.mortgageInterest_O55 = this.MortgageValue(
          inputs,
          outputs,
          1,
          25,
          36
        );
        outputs.proformasYear4.capitalReservesPerUnit_O57 =
          outputs.proformasYear3.capitalReservesPerUnit_N57 *
          (1 + inputs.proformaYear4ExpenseChange_O35 / 100);
        outputs.proformasYear4.capitalReserves_O58 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear4.capitalReservesPerUnit_O57;
        outputs.proformasYear4.preTaxProfit_O60 =
          outputs.proformasYear4.netOperatingIncome_O53 -
          outputs.proformasYear4.mortgageInterest_O55 -
          outputs.proformasYear4.capitalReserves_O58;
        outputs.proformasYear4.preTaxROI_O61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear4.preTaxProfit_O60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear4.assetManagement_O63 =
          outputs.proformasYear4.totalNetIncome_O46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.proformasYear4.principalReduction_O64 = this.MortgageValue(
          inputs,
          outputs,
          0,
          25,
          36
        );
        outputs.proformasYear4.debtService_O65 =
          outputs.proformasYear4.mortgageInterest_O55 +
          outputs.proformasYear4.principalReduction_O64;
        outputs.proformasYear4.debtServiceCoverageRatio_O66 =
          outputs.proformasYear4.debtService_O65 == 0
            ? 0
            : (outputs.proformasYear4.netOperatingIncome_O53 -
                outputs.proformasYear4.capitalReserves_O58) /
              outputs.proformasYear4.debtService_O65;
        outputs.proformasYear4.netCashflowAnnual_O68 =
          outputs.proformasYear4.preTaxProfit_O60 -
          outputs.proformasYear4.principalReduction_O64 -
          outputs.proformasYear4.assetManagement_O63;
        outputs.proformasYear4.netROICashOnCash_O70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear4.netCashflowAnnual_O68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear4.leadInvestorCompensation_O72 =
          outputs.proformasYear4.netCashflowAnnual_O68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.proformasYear4.netToPassives_O73 =
          outputs.proformasYear4.netCashflowAnnual_O68 -
          outputs.proformasYear4.leadInvestorCompensation_O72;
        outputs.proformasYear4.netROIToPassives_O74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear4.netToPassives_O73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear4.capRateProjection_O76 =
          inputs.proformasYear4.capRateProjection_O76;
        outputs.proformasYear4.brokerPriceOpinionProjection_O78 =
          outputs.proformasYear4.capRateProjection_O76 == 0
            ? 0
            : outputs.proformasYear4.netOperatingIncome_O53 /
              (outputs.proformasYear4.capRateProjection_O76 / 100);
        // Proforma Year 5.
        outputs.proformasYear5.label_P38 = inputs.proformasYear5.label;
        outputs.proformasYear5.grossRentalIncome_P40 =
          outputs.proformasYear4.grossRentalIncome_O40 *
          (1 + inputs.proformaYear5IncomeChange_P34 / 100);
        outputs.proformasYear5.economicVacancy_P41 =
          outputs.proformasYear5.grossRentalIncome_P40 *
          (inputs.proformasYear5.economicVacancyRate_P42 / 100);
        outputs.proformasYear5.economicVacancyRatePercent_P42 =
          inputs.proformasYear5.economicVacancyRate_P42;
        outputs.proformasYear5.netRentalIncome_P43 =
          outputs.proformasYear5.grossRentalIncome_P40 -
          outputs.proformasYear5.economicVacancy_P41;
        outputs.proformasYear5.laundry_P44 =
          outputs.proformasYear4.laundry_O44 *
          (1 + inputs.proformaYear5IncomeChange_P34 / 100);
        outputs.proformasYear5.rubs =
          outputs.proformasYear4.rubs *
          (1 + inputs.proformaYear5IncomeChange_P34 / 100);
        outputs.proformasYear5.miscellaneousOtherIncome_P45 =
          outputs.proformasYear4.miscellaneousOtherIncome_O45 *
          (1 + inputs.proformaYear5IncomeChange_P34 / 100);
        outputs.proformasYear5.totalNetIncome_P46 =
          outputs.proformasYear5.netRentalIncome_P43 +
          outputs.proformasYear5.laundry_P44 +
          outputs.proformasYear5.rubs +
          outputs.proformasYear5.miscellaneousOtherIncome_P45;
        outputs.proformasYear5.expensesPerUnit_P48 =
          outputs.proformasYear4.expensesPerUnit_O48 *
          (1 + inputs.proformaYear5ExpenseChange_P35 / 100);
        outputs.proformasYear5.expensesPerSqft_P49 =
          avgUnitSqft == 0
            ? 0
            : outputs.proformasYear5.totalExpenses_P51 / avgUnitSqft;
        outputs.proformasYear5.expensesAsPercentOfIncome_P50 =
          outputs.proformasYear5.totalNetIncome_P46 == 0
            ? 0
            : (outputs.proformasYear5.totalExpenses_P51 /
                outputs.proformasYear5.totalNetIncome_P46) *
              100;
        outputs.proformasYear5.totalExpenses_P51 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear5.expensesPerUnit_P48;
        outputs.proformasYear5.netOperatingIncome_P53 =
          outputs.proformasYear5.totalNetIncome_P46 -
          outputs.proformasYear5.totalExpenses_P51;
        outputs.proformasYear5.mortgageInterest_P55 = this.MortgageValue(
          inputs,
          outputs,
          1,
          25,
          36
        );
        outputs.proformasYear5.capitalReservesPerUnit_P57 =
          outputs.proformasYear4.capitalReservesPerUnit_O57 *
          (1 + inputs.proformaYear5ExpenseChange_P35 / 100);
        outputs.proformasYear5.capitalReserves_P58 =
          outputs.numberOfUnits_J28 *
          outputs.proformasYear5.capitalReservesPerUnit_P57;
        outputs.proformasYear5.preTaxProfit_P60 =
          outputs.proformasYear5.netOperatingIncome_P53 -
          outputs.proformasYear5.mortgageInterest_P55 -
          outputs.proformasYear5.capitalReserves_P58;
        outputs.proformasYear5.preTaxROI_P61 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear5.preTaxProfit_P60 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear5.assetManagement_P63 =
          outputs.proformasYear5.totalNetIncome_P46 *
          (outputs.assetManagementPercent_I63 / 100);
        outputs.proformasYear5.principalReduction_P64 = this.MortgageValue(
          inputs,
          outputs,
          0,
          25,
          36
        );
        outputs.proformasYear5.debtService_P65 =
          outputs.proformasYear5.mortgageInterest_P55 +
          outputs.proformasYear5.principalReduction_P64;
        outputs.proformasYear5.debtServiceCoverageRatio_P66 =
          outputs.proformasYear5.debtService_P65 == 0
            ? 0
            : (outputs.proformasYear5.netOperatingIncome_P53 -
                outputs.proformasYear5.capitalReserves_P58) /
              outputs.proformasYear5.debtService_P65;
        outputs.proformasYear5.netCashflowAnnual_P68 =
          outputs.proformasYear5.preTaxProfit_P60 -
          outputs.proformasYear5.principalReduction_P64 -
          outputs.proformasYear5.assetManagement_P63;
        outputs.proformasYear5.netROICashOnCash_P70 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear5.netCashflowAnnual_P68 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear5.leadInvestorCompensation_P72 =
          outputs.proformasYear5.netCashflowAnnual_P68 *
          (outputs.leadInvestorCompensationPercent_I72 / 100);
        outputs.proformasYear5.netToPassives_P73 =
          outputs.proformasYear5.netCashflowAnnual_P68 -
          outputs.proformasYear5.leadInvestorCompensation_P72;
        outputs.proformasYear5.netROIToPassives_P74 =
          outputs.totalOutOfPocket_J26 == 0
            ? 0
            : (outputs.proformasYear5.netToPassives_P73 /
                outputs.totalOutOfPocket_J26) *
              100;
        outputs.proformasYear5.capRateProjection_P76 =
          inputs.proformasYear5.capRateProjection_P76;
        outputs.proformasYear5.brokerPriceOpinionProjection_P78 =
          outputs.proformasYear5.capRateProjection_P76 == 0
            ? 0
            : outputs.proformasYear5.netOperatingIncome_P53 /
              (outputs.proformasYear5.capRateProjection_P76 / 100);
        // Notes.
        outputs.notes_H80 = inputs.notes_H80;
      }
    }
  }
};
</script>

<style>
table {
  font-size: 90%;
}
input[type="number"] {
  text-align: right;
}
input[type="text"] {
  text-align: left;
}
td.operating-expenses-columns-header {
  font-weight: bold;
  font-size: 120%;
  text-align: center;
}
td.operating-expenses-column-header {
  font-weight: bold;
  font-size: 100%;
  text-align: right;
}
td.align-left {
  text-align: left;
}
td.align-right {
  text-align: right;
}
td.column-header {
  font-weight: bold;
  font-size: 120%;
  vertical-align: bottom;
}
tr.subline {
  font-weight: lighter;
  font-size: 90%;
}
tr.subtotal1 {
  font-weight: bold;
  font-size: 90%;
  border-top: 2px solid;
  border-bottom: 2px solid;
}
tr.subtotal2 {
  font-weight: bold;
  font-size: 110%;
  border-top: 2px solid;
  border-bottom: 2px solid;
}
tr.subtotal3 {
  font-weight: bold;
  outline: 2px solid;
  font-size: 120%;
  text-transform: uppercase;
}
.disclaimer {
  font-size: 80%;
  font-weight: bold;
}
.textarea {
  width: 250px;
  min-height: 50px;
  height: auto;
  border: 2px solid rgba(63, 63, 63, 1);
}
.notices .snackbar {
  min-height: 2.5em;
  min-width: 1em;
}
div.ql-editor {
  background-color: #ffdd57 !important;
}
</style>
